import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Menu,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Chip,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Repeat,
  Receipt,
  AttachMoney,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  RadioButtonUnchecked,
  AttachFile,
  InsertDriveFile,
  Close,
  InfoOutlined,
  Save,
  PictureAsPdf,
  Lock,
  LockOpen,
  Refresh,
  Assessment,
  Download as DownloadIcon
} from '@mui/icons-material';
import { apiService } from '@/services/api';
import { formatCurrency, parseCurrency, numberToEditableString } from '@/utils/formatMask';
import { useMessage } from '@/contexts/MessageContext';
import { useRefresh } from '@/contexts/RefreshContext';
import { useFilters } from '@/contexts/FilterContext';
import { useConfirmation } from '@/contexts/ConfirmationContext';
import { logger } from '@/utils/logger';
import { Link as RouterLink } from 'react-router-dom';
import Link from '@mui/material/Link';
import { NfseBiguacuHomologacaoMock } from '@/components/nfse/NfseBiguacuHomologacaoMock';
import { buildBiguacuHomologacaoProps } from '@/components/nfse/buildBiguacuHomologacaoProps';
import NfseNacionalDanfsePreview from '@/components/nfse/NfseNacionalDanfsePreview';
import NfseEmissionFloatingStatus, {
  type EmissionStatus
} from '@/components/nfse/NfseEmissionFloatingStatus';
import { buildNacionalDanfseProps } from '@/components/nfse/buildNacionalDanfseProps';
import { ClientCell } from '@/components/ClientCell';
import { CopyableId } from '@/components/CopyableId';
import { ProductCell } from '@/components/ProductCell';
import { useProductsMap } from '@/hooks/useProductsMap';
import { ProductScopeStatePanel, errorToPanel } from '@/components/ProductScopeStatePanel';
import { parseLoadError } from '@/utils/productScope';

const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro'
];

const MONTH_SHORT = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez'
];

const formatMonthYearRef = (m: number, y: number) =>
  m >= 1 && m <= 12 ? `${MONTH_SHORT[m - 1]}/${y}` : '-';

/** Intervalo civil para listagem admin no billing (dueDate). */
function periodBounds(
  year: number,
  month: number,
  viewMode: 'month' | 'year'
): { from: string; to: string } {
  if (viewMode === 'month') {
    const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const pad = (n: number) => String(n).padStart(2, '0');
    return {
      from: `${year}-${pad(month)}-01`,
      to: `${year}-${pad(month)}-${pad(last)}`
    };
  }
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

const getExpenseDueDate = (entry: { month?: number; year?: number; dueDayOfMonth?: number }) => {
  const m = entry.month ?? 0;
  const y = entry.year ?? 0;
  const d = entry.dueDayOfMonth ?? 31;
  if (!m || !y) return null;
  const lastDay = new Date(y, m, 0).getDate();
  const day = Math.min(d, lastDay);
  return new Date(y, m - 1, day);
};

const isExpenseOverdue = (entry: {
  paid?: boolean;
  month?: number;
  year?: number;
  dueDayOfMonth?: number;
}) => {
  if (entry.paid) return false;
  const due = getExpenseDueDate(entry);
  return due ? due < new Date() : false;
};

export interface ExpenseEntry {
  _id: string;
  recurringExpenseId?: string;
  description: string;
  amountBRL: number;
  wasAdjusted: boolean;
  paid?: boolean;
  paidAt?: string;
  invoices?: Array<{ key: string; fileName: string; uploadedAt: string }>;
  paymentReceipts?: Array<{ key: string; fileName: string; uploadedAt: string }>;
  dueDayOfMonth?: number;
  source: 'recurring' | 'manual';
  month?: number;
  year?: number;
}

/** Linha da listagem admin de faturas (vover-billing). */
export interface BillingInvoiceRow {
  id: string;
  productId?: string;
  tenantId: string;
  status: string;
  amountCents: number;
  currency?: string;
  dueDate?: string;
  paidAt?: string;
  billingPeriod?: string;
  description?: string;
  paymentId?: string;
  paymentProofUrl?: string;
  hasInterReceipt?: boolean;
  interReceiptAt?: string;
  nfse?: {
    status?: string;
    pdfUrl?: string;
    xmlUrl?: string;
    numero?: string;
    codigoVerificacao?: string;
  };
}

export type RecurrencePeriod = 'monthly' | 'quarterly' | 'semiannual' | 'annual';

export const RECURRENCE_PERIOD_OPTIONS: Array<{ value: RecurrencePeriod; label: string }> = [
  { value: 'monthly', label: 'Mensal' },
  { value: 'quarterly', label: 'Trimestral' },
  { value: 'semiannual', label: 'Semestral' },
  { value: 'annual', label: 'Anual' }
];

function recurrencePeriodLabel(period?: RecurrencePeriod): string {
  return RECURRENCE_PERIOD_OPTIONS.find(o => o.value === period)?.label ?? 'Mensal';
}

function formatRecurrenceStartDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR');
}

function defaultRecurrenceStartDateValue(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
}

function recurrenceStartDateFromExpense(r: RecurringExpense): string {
  if (!r.recurrenceStartDate) return defaultRecurrenceStartDateValue();
  const d = new Date(r.recurrenceStartDate);
  if (Number.isNaN(d.getTime())) return defaultRecurrenceStartDateValue();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export interface RecurringExpense {
  _id: string;
  description: string;
  defaultAmountBRL: number;
  dueDayOfMonth: number;
  recurrencePeriod?: RecurrencePeriod;
  recurrenceStartDate?: string;
  isActive: boolean;
}

export interface RevenueByTenant {
  tenantId: string;
  companyName: string;
  packageType: string;
  productId?: string;
  monthlyAmountBRL: number;
  breakdown?: {
    basePrice: number;
    clientCostSubtotal: number;
    volumeDiscount: number;
    clientCostAfterDiscount: number;
    extraUsersCost: number;
    totalUnits?: number;
    totalClients?: number;
    perClientBreakdown?: Array<{
      clientId?: string;
      clientName: string;
      units: number;
      subtotal: number;
      total: number;
    }>;
  };
  /** Tenant trancado por pagamento da fatura desta compet\u00eancia. */
  lockedByInvoiceIds?: string[];
  lockedAt?: string;
  lockSource?: 'invoice-paid';
}

interface ManualRevenueTenantOption {
  tenantId: string;
  companyName: string;
  trialEndsAt?: string | undefined;
  subscriptionStartsAt?: string | undefined;
  createdAt?: string | undefined;
}

interface ManualRevenuePeriodOption {
  key: string; // YYYY-MM
  label: string; // MM-YYYY
}

const pad2 = (n: number) => String(n).padStart(2, '0');
const toIsoMonth = (yearValue: number, monthValue: number) => `${yearValue}-${pad2(monthValue)}`;
const toMonthYearLabel = (yearValue: number, monthValue: number) =>
  `${pad2(monthValue)}-${yearValue}`;

const parseIsoMonth = (value: string): { year: number; month: number } | null => {
  const m = /^(\d{4})-(\d{2})$/.exec(value);
  if (!m) return null;
  const yearValue = Number(m[1]);
  const monthValue = Number(m[2]);
  if (!yearValue || monthValue < 1 || monthValue > 12) return null;
  return { year: yearValue, month: monthValue };
};

function formatXmlForPreview(xml: string): string {
  const raw = String(xml || '').trim();
  if (!raw) return '';
  // Formatação simples (sem dependências) para leitura humana no modal.
  const spaced = raw.replace(/>\s*</g, '>\n<');
  const lines = spaced.split('\n');
  let indent = 0;
  const pad = (n: number) => '  '.repeat(Math.max(0, n));
  return lines
    .map(line => {
      const l = line.trim();
      if (!l) return '';
      const isClosing = /^<\/.+>/.test(l);
      const isSelfClosing = /\/>$/.test(l) || /^<\?xml/.test(l) || /^<!/.test(l);
      const isOpening = /^<[^/!?][^>]*>$/.test(l) && !isSelfClosing;
      if (isClosing) indent = Math.max(0, indent - 1);
      const out = `${pad(indent)}${l}`;
      if (isOpening) indent += 1;
      return out;
    })
    .filter(Boolean)
    .join('\n');
}

export const FinancialFlowPage: React.FC = () => {
  const { selectedProductIds } = useFilters();
  const selectedSingleProductId = selectedProductIds.length === 1 ? selectedProductIds[0]! : '';
  const isMultiProduct = selectedProductIds.length > 1;
  const productsById = useProductsMap();
  const now = new Date();
  const productOptions = useMemo(() => Array.from(productsById.values()), [productsById]);
  const [viewMode, setViewMode] = useState<'month' | 'year'>('month');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dashboardLoadError, setDashboardLoadError] = useState<unknown>(null);
  const [summary, setSummary] = useState<{
    totalExpenses: number;
    totalExpensesPaid: number;
    totalRevenues: number;
    totalRevenuesReceived: number;
    balance: number;
  } | null>(null);
  const [lifetime, setLifetime] = useState<{
    totalExpensesPaid: number;
    totalRevenuesReceived: number;
    balance: number;
  } | null>(null);
  const [expenses, setExpenses] = useState<ExpenseEntry[]>([]);
  const [revenues, setRevenues] = useState<{
    totalMonthlyBRL: number;
    byTenant: RevenueByTenant[];
    competence?: string;
    isClosed?: boolean;
    closedAt?: string | null;
    source?: 'realtime' | 'auto-cron' | 'lazy-read' | 'manual' | 'manual-seed' | 'reopen-recalc';
    reopenedAt?: string;
    reopenedBy?: string;
    reopenCount?: number;
    notes?: string;
  } | null>(null);
  const [recurringExpenses, setRecurringExpenses] = useState<RecurringExpense[]>([]);

  const [billingInvoices, setBillingInvoices] = useState<BillingInvoiceRow[]>([]);
  const [billingListLoading, setBillingListLoading] = useState(false);
  const [billingListError, setBillingListError] = useState<string | null>(null);
  const [billingNextCursor, setBillingNextCursor] = useState<string | null>(null);
  const [billingLoadingMore, setBillingLoadingMore] = useState(false);
  const [expandedProjectedTenants, setExpandedProjectedTenants] = useState<Set<string>>(new Set());
  const [tenantInfoById, setTenantInfoById] = useState<
    Record<string, { companyName: string | null; logoUrl: string | null; cnpj: string | null }>
  >({});
  const [tenantInfoLoading, setTenantInfoLoading] = useState(false);
  /** Busca pontual GET /tenants?tenantIds=… para linhas da lista billing. */
  const [billingTenantResolveBusy, setBillingTenantResolveBusy] = useState(false);

  /** Tenant IDs presentes nas faturas da lista (para buscar nomes que não vieram na página inicial). */
  const billingTenantIds = useMemo(
    () => [...new Set(billingInvoices.map(r => String(r.tenantId ?? '').trim()).filter(Boolean))],
    [billingInvoices]
  );

  const [editRevenueInvoiceId, setEditRevenueInvoiceId] = useState<string | null>(null);
  const [editRevenueLoading, setEditRevenueLoading] = useState(false);
  const [editRevenueSaving, setEditRevenueSaving] = useState(false);
  const [editRevenueTenantId, setEditRevenueTenantId] = useState('');
  const [editRevenueBillingPeriod, setEditRevenueBillingPeriod] = useState('');
  const [editRevenueDueDate, setEditRevenueDueDate] = useState(''); // YYYY-MM-DD
  const [editRevenueAmountRaw, setEditRevenueAmountRaw] = useState('');
  const [editRevenueStatus, setEditRevenueStatus] = useState<
    'draft' | 'pending' | 'paid' | 'overdue' | 'cancelled'
  >('paid');
  const [editRevenuePaidAt, setEditRevenuePaidAt] = useState(''); // datetime-local
  const [editRevenueDescription, setEditRevenueDescription] = useState('');
  const [editRevenueNote, setEditRevenueNote] = useState('');
  const [editRevenueMetadata, setEditRevenueMetadata] = useState<any>({});

  const [refreshingReceiptByInvoice, setRefreshingReceiptByInvoice] = useState<
    Record<string, boolean>
  >({});

  const [nfPreviewInvoiceId, setNfPreviewInvoiceId] = useState<string | null>(null);
  /** Discriminação editada no modal; `null` = usar a sugerida pelo billing. */
  const [nfseDescriptionDraft, setNfseDescriptionDraft] = useState<string | null>(null);
  /** Resultado da última tentativa de emissão exibida no modal (etapas + erros do IPM). */
  const [nfseIssueResult, setNfseIssueResult] = useState<{
    outcome: 'success' | 'pending' | 'rejected' | 'no-response';
    testMode?: boolean | undefined;
    ipmAccepted?: boolean | undefined;
    provider?: 'ipm' | 'nacional' | undefined;
    persisted?: boolean | undefined;
    numero?: string | undefined;
    reason?: string | undefined;
    messages: Array<{ codigo?: string; mensagem?: string; correcao?: string }>;
  } | null>(null);
  useEffect(() => {
    setNfseDescriptionDraft(null);
    setNfseIssueResult(null);
  }, [nfPreviewInvoiceId]);
  const [issuingNfByInvoice, setIssuingNfByInvoice] = useState<Record<string, boolean>>({});
  /** Painel flutuante da emissão: visível até concluir; erros ficam até o usuário fechar. */
  const [nfseEmissionStatus, setNfseEmissionStatus] = useState<EmissionStatus | null>(null);
  const [syncingNfFromIpmByInvoice, setSyncingNfFromIpmByInvoice] = useState<
    Record<string, boolean>
  >({});
  /** Diálogo: consulta IPM com RPS opcional (quando não há `nfse_audit` com o número usado na emissão). */
  const [ipmSyncDialogInvoiceId, setIpmSyncDialogInvoiceId] = useState<string | null>(null);
  const [ipmSyncRpsInput, setIpmSyncRpsInput] = useState('');
  const [nfsePayloadByInvoiceId, setNfsePayloadByInvoiceId] = useState<
    Record<string, unknown | null>
  >({});
  const [nfsePayloadLoadingByInvoiceId, setNfsePayloadLoadingByInvoiceId] = useState<
    Record<string, boolean>
  >({});
  const [nfsePayloadErrorByInvoiceId, setNfsePayloadErrorByInvoiceId] = useState<
    Record<string, string | null>
  >({});
  /** Config global billing (beneficiário / ISS) para mock visual da NFS-e */
  const [nfPreviewBillingCfg, setNfPreviewBillingCfg] = useState<Record<string, unknown> | null>(
    null
  );

  const nfPreviewGate = useMemo(() => {
    const id = nfPreviewInvoiceId;
    if (!id) {
      return { issueBlocked: true, validationErrors: [] as string[], showConfigHint: false };
    }
    if (nfsePayloadLoadingByInvoiceId[id]) {
      return { issueBlocked: true, validationErrors: [] as string[], showConfigHint: false };
    }
    if (nfsePayloadErrorByInvoiceId[id]) {
      return { issueBlocked: true, validationErrors: [] as string[], showConfigHint: true };
    }
    const p = nfsePayloadByInvoiceId[id];
    if (p == null || typeof p !== 'object') {
      return { issueBlocked: true, validationErrors: [] as string[], showConfigHint: false };
    }
    const rawErrs = (p as { validationErrors?: unknown }).validationErrors;
    const validationErrors = Array.isArray(rawErrs)
      ? rawErrs.filter((x): x is string => typeof x === 'string')
      : [];
    const success = (p as { success?: boolean }).success === true;
    const issueBlocked = !success || validationErrors.length > 0;
    return {
      issueBlocked,
      validationErrors,
      showConfigHint: issueBlocked
    };
  }, [
    nfPreviewInvoiceId,
    nfsePayloadByInvoiceId,
    nfsePayloadLoadingByInvoiceId,
    nfsePayloadErrorByInvoiceId
  ]);

  // Refs para evitar dependências circulares no useEffect de prévia NFSe
  const nfsePayloadByInvoiceIdRef = useRef(nfsePayloadByInvoiceId);
  const nfsePayloadLoadingByInvoiceIdRef = useRef(nfsePayloadLoadingByInvoiceId);
  useEffect(() => {
    nfsePayloadByInvoiceIdRef.current = nfsePayloadByInvoiceId;
  }, [nfsePayloadByInvoiceId]);
  useEffect(() => {
    nfsePayloadLoadingByInvoiceIdRef.current = nfsePayloadLoadingByInvoiceId;
  }, [nfsePayloadLoadingByInvoiceId]);

  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [adjustEntry, setAdjustEntry] = useState<ExpenseEntry | null>(null);
  const [adjustAmountRaw, setAdjustAmountRaw] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualDesc, setManualDesc] = useState('');
  const [manualAmountRaw, setManualAmountRaw] = useState('');
  const [manualDay, setManualDay] = useState(1);
  const [manualMonth, setManualMonth] = useState(1);
  const [manualYear, setManualYear] = useState(now.getFullYear());
  const [manualRecurringId, setManualRecurringId] = useState<string>('');
  const [creatingManual, setCreatingManual] = useState(false);

  const [manualRevenueOpen, setManualRevenueOpen] = useState(false);
  const [manualRevenueApplicationId, setManualRevenueApplicationId] = useState('');
  const [manualRevenueTenantId, setManualRevenueTenantId] = useState('');
  const [manualRevenueBillingPeriod, setManualRevenueBillingPeriod] = useState('');
  const [manualRevenueDueDate, setManualRevenueDueDate] = useState('');
  const [manualRevenueAmountRaw, setManualRevenueAmountRaw] = useState('');
  const [manualRevenueStatus, setManualRevenueStatus] = useState<
    'draft' | 'pending' | 'paid' | 'overdue' | 'cancelled'
  >('paid');
  const [manualRevenuePaidAt, setManualRevenuePaidAt] = useState('');
  const [manualRevenueDescription, setManualRevenueDescription] = useState('');
  const [manualRevenueNote, setManualRevenueNote] = useState('');
  const [manualRevenueSaving, setManualRevenueSaving] = useState(false);
  const [manualRevenueTenants, setManualRevenueTenants] = useState<ManualRevenueTenantOption[]>([]);
  const [manualRevenueDueDayMap, setManualRevenueDueDayMap] = useState<Record<string, number>>({});
  const [manualRevenuePeriods, setManualRevenuePeriods] = useState<ManualRevenuePeriodOption[]>([]);

  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
  const [recurringEdit, setRecurringEdit] = useState<RecurringExpense | null>(null);
  const [recurringDesc, setRecurringDesc] = useState('');
  const [recurringAmountRaw, setRecurringAmountRaw] = useState('');
  const [recurringDay, setRecurringDay] = useState(1);
  const [recurringPeriod, setRecurringPeriod] = useState<RecurrencePeriod>('monthly');
  const [recurringStartDate, setRecurringStartDate] = useState(defaultRecurrenceStartDateValue);
  const [savingRecurring, setSavingRecurring] = useState(false);

  const [expenseInvoiceAnchor, setExpenseInvoiceAnchor] = useState<{
    el: HTMLElement;
    entry: ExpenseEntry;
  } | null>(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [uploadType, setUploadType] = useState<'invoice' | 'payment_receipt'>('invoice');

  const [paymentDateModal, setPaymentDateModal] = useState<{ entry: ExpenseEntry } | null>(null);
  const [paymentDateValue, setPaymentDateValue] = useState('');
  const [submittingPaymentDate, setSubmittingPaymentDate] = useState(false);

  const [processExpensesModalOpen, setProcessExpensesModalOpen] = useState(false);
  const [selectedRecurringIds, setSelectedRecurringIds] = useState<Set<string>>(new Set());
  const [processExpenseAmounts, setProcessExpenseAmounts] = useState<Record<string, string>>({});
  const [processingExpenses, setProcessingExpenses] = useState(false);
  const expenseFileInputRef = useRef<HTMLInputElement>(null);

  const { showSuccess, showError } = useMessage();
  const { setRefreshCallback } = useRefresh();
  const { confirm } = useConfirmation();

  // ===== MRR snapshots =====
  const [seedDialogOpen, setSeedDialogOpen] = useState(false);
  const [seedCompetenceInput, setSeedCompetenceInput] = useState('');
  const [seedEstimatedRaw, setSeedEstimatedRaw] = useState('');
  const [seedActiveTenants, setSeedActiveTenants] = useState<string>('');
  const [seedTrialTenants, setSeedTrialTenants] = useState<string>('');
  const [seedNotes, setSeedNotes] = useState('');
  const [seedSaving, setSeedSaving] = useState(false);

  const [reopenSubmitting, setReopenSubmitting] = useState(false);
  const [closeSubmitting, setCloseSubmitting] = useState(false);

  const [yearReport, setYearReport] = useState<{
    year: number;
    totals: { estimatedMRR: number; paidAmount: number; diff: number; achievedPct: number };
    rows: Array<{
      competence: string;
      isClosed: boolean;
      closedAt: string | null;
      source: string;
      estimatedMRR: number;
      paidAmount: number;
      diff: number;
      achievedPct: number;
      totals: {
        estimatedMRR: number;
        activeTenants: number;
        trialTenants: number;
        byPackageType: { condominio: number; gestao: number; institucional: number };
      };
      perTenant?: Array<{
        tenantId: string;
        tenantName: string;
        packageType: 'condominio' | 'gestao' | 'institucional';
        productId?: string;
        status: 'active' | 'trial';
        monthlyAmount: number;
        totalUnits: number;
        totalClients: number;
        lockedByInvoiceIds?: string[];
        lockedAt?: string;
        lockSource?: 'invoice-paid';
      }>;
    }>;
  } | null>(null);
  const [yearReportLoading, setYearReportLoading] = useState(false);
  const yearReportIncludePerTenant = true;
  const [expandedYearRows, setExpandedYearRows] = useState<Set<string>>(new Set());

  const loadData = useCallback(async () => {
    setLoading(true);
    setDashboardLoadError(null);
    try {
      const monthParam = viewMode === 'month' ? month : undefined;
      const res = await apiService.getFinancialDashboard(year, monthParam);

      if (res.data.success && res.data.data) {
        const d = res.data.data;
        setSummary(d.summary);
        setLifetime(d.lifetime ?? null);
        setExpenses(d.expenses ?? []);
        setRevenues(d.revenues ?? null);
        setRecurringExpenses(d.recurringExpenses ?? []);
      } else {
        setDashboardLoadError(new Error('Resposta inválida do fluxo financeiro'));
      }
    } catch (err) {
      setDashboardLoadError(err);
      logger.error('Error loading financial flow', err);
    } finally {
      setLoading(false);
    }
    // loadData só recria quando year/month/viewMode mudam; showError omitido para evitar refresh em loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, viewMode, selectedProductIds]);

  const loadYearReport = useCallback(async () => {
    setYearReportLoading(true);
    try {
      const res = await apiService.getMrrYearReport(year, yearReportIncludePerTenant);
      if (res.data?.success && res.data.data) {
        setYearReport(res.data.data);
      } else {
        showError('Erro ao carregar relatório anual');
      }
    } catch (err) {
      logger.error('Error loading MRR year report', err);
      showError('Erro ao carregar relatório anual');
    } finally {
      setYearReportLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, yearReportIncludePerTenant]);

  const handleReopenCompetence = useCallback(() => {
    if (!revenues?.competence) return;
    const competence = revenues.competence;
    confirm({
      title: 'Reabrir competência',
      message: `Recalcular o MRR previsto para ${competence}? Isto sobrescreve o snapshot existente. A acção fica registada na auditoria.`,
      confirmText: 'Reabrir e recalcular',
      confirmColor: 'warning',
      onConfirm: async () => {
        setReopenSubmitting(true);
        try {
          await apiService.reopenMrrCompetence(competence);
          showSuccess('Competência reaberta e recalculada');
          await loadData();
        } catch (err) {
          logger.error('reopen competence', err);
          showError('Erro ao reabrir competência');
        } finally {
          setReopenSubmitting(false);
        }
      }
    });
  }, [revenues?.competence, confirm, loadData, showError, showSuccess]);

  const handleCloseCompetence = useCallback(() => {
    if (!revenues?.competence) return;
    const competence = revenues.competence;
    confirm({
      title: 'Fechar competência',
      message: `Fechar manualmente a competência ${competence}? O sistema grava um snapshot auditável do MRR estimado atual, calculado a partir da precificação e clientes do período. Depois disso, a competência deixa de mudar com novos clientes ou alterações de preço; reabrir/recalcular só deve ser usado para correção operacional.`,
      confirmText: 'Fechar agora',
      confirmColor: 'warning',
      onConfirm: async () => {
        setCloseSubmitting(true);
        try {
          await apiService.closeMrrCompetence(competence);
          showSuccess('Competência fechada');
          await loadData();
        } catch (err) {
          logger.error('close competence', err);
          showError('Erro ao fechar competência');
        } finally {
          setCloseSubmitting(false);
        }
      }
    });
  }, [revenues?.competence, confirm, loadData, showError, showSuccess]);

  const handleOpenSeed = useCallback(() => {
    const target = revenues?.competence ?? `${year}-${String(month).padStart(2, '0')}`;
    setSeedCompetenceInput(target);
    setSeedEstimatedRaw('');
    setSeedActiveTenants('');
    setSeedTrialTenants('');
    setSeedNotes('');
    setSeedDialogOpen(true);
  }, [revenues?.competence, year, month]);

  const handleSubmitSeed = useCallback(async () => {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(seedCompetenceInput)) {
      showError('Competência inválida (use YYYY-MM)');
      return;
    }
    const estimatedMRR = parseCurrency(seedEstimatedRaw);
    if (!Number.isFinite(estimatedMRR) || estimatedMRR < 0) {
      showError('MRR previsto inválido');
      return;
    }
    setSeedSaving(true);
    try {
      const payload: {
        estimatedMRR: number;
        activeTenants?: number;
        trialTenants?: number;
        notes?: string;
      } = { estimatedMRR };
      if (seedActiveTenants.trim()) payload.activeTenants = parseInt(seedActiveTenants, 10);
      if (seedTrialTenants.trim()) payload.trialTenants = parseInt(seedTrialTenants, 10);
      if (seedNotes.trim()) payload.notes = seedNotes.trim();
      await apiService.seedMrrCompetence(seedCompetenceInput, payload);
      showSuccess('Snapshot histórico gravado');
      setSeedDialogOpen(false);
      await loadData();
      if (tabValue === 3) await loadYearReport();
    } catch (err: any) {
      logger.error('seed competence', err);
      const msg = err?.response?.data?.message || err?.message || 'Erro ao gravar snapshot';
      showError(msg);
    } finally {
      setSeedSaving(false);
    }
  }, [
    seedCompetenceInput,
    seedEstimatedRaw,
    seedActiveTenants,
    seedTrialTenants,
    seedNotes,
    showError,
    showSuccess,
    loadData,
    tabValue,
    loadYearReport
  ]);

  const exportYearReportCsv = useCallback(() => {
    if (!yearReport) return;
    const lines: string[] = [];
    lines.push(
      [
        'Competência',
        'Status',
        'Source',
        'Tenants ativos',
        'Tenants trial',
        'MRR previsto (R$)',
        'Recebido (R$)',
        'Diferença (R$)',
        '% atingido',
        'Condomínio',
        'Gestão',
        'Institucional'
      ].join(';')
    );
    for (const r of yearReport.rows) {
      lines.push(
        [
          r.competence,
          r.isClosed ? 'fechado' : 'aberto',
          r.source,
          r.totals?.activeTenants ?? 0,
          r.totals?.trialTenants ?? 0,
          r.estimatedMRR.toFixed(2).replace('.', ','),
          r.paidAmount.toFixed(2).replace('.', ','),
          r.diff.toFixed(2).replace('.', ','),
          r.achievedPct.toFixed(1).replace('.', ','),
          (r.totals?.byPackageType?.condominio ?? 0).toFixed(2).replace('.', ','),
          (r.totals?.byPackageType?.gestao ?? 0).toFixed(2).replace('.', ','),
          (r.totals?.byPackageType?.institucional ?? 0).toFixed(2).replace('.', ',')
        ].join(';')
      );
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mrr-relatorio-${yearReport.year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [yearReport]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setRefreshCallback(loadData);
    return () => setRefreshCallback(null);
  }, [loadData, setRefreshCallback]);

  useEffect(() => {
    if (tabValue === 3) {
      void loadYearReport();
    }
  }, [tabValue, loadYearReport]);

  const loadBillingInvoices = useCallback(
    async (append: boolean, cursor?: string) => {
      if (!append) {
        setBillingListLoading(true);
        setBillingListError(null);
      } else {
        setBillingLoadingMore(true);
      }
      try {
        const pad2 = (n: number) => String(n).padStart(2, '0');
        const billingPeriod = viewMode === 'month' ? `${year}-${pad2(month)}` : undefined;
        const billingPeriodPrefix = viewMode === 'year' ? `${year}-` : undefined;
        const res = await apiService.listBillingInvoices({
          // Lista da aba "Receitas": filtrar pelo período de cobrança (billingPeriod),
          // porque o vencimento pode cair no mês seguinte (ex.: período 04/2026 vence em 05/2026).
          ...(billingPeriod ? { billingPeriod } : {}),
          ...(billingPeriodPrefix ? { billingPeriodPrefix } : {}),
          limit: 100,
          ...(cursor ? { starting_after: cursor } : {})
        });
        const payload = res.data as {
          object?: string;
          data?: BillingInvoiceRow[];
          has_more?: boolean;
          next_cursor?: string | null;
          success?: boolean;
          message?: string;
          detail?: string;
        };
        if (res.status >= 400) {
          setBillingListError(
            payload?.message ||
              payload?.detail ||
              'Billing indisponível ou não configurado (servidor).'
          );
          if (!append) setBillingInvoices([]);
          return;
        }
        const rows = payload?.data ?? [];
        if (append) setBillingInvoices(prev => [...prev, ...rows]);
        else setBillingInvoices(rows);
        setBillingNextCursor(payload?.next_cursor ?? null);
      } catch {
        setBillingListError('Erro ao carregar faturas do billing.');
        if (!append) setBillingInvoices([]);
      } finally {
        setBillingListLoading(false);
        setBillingLoadingMore(false);
      }
    },
    [year, month, viewMode]
  );

  useEffect(() => {
    if (tabValue !== 1) return;
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await loadBillingInvoices(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [tabValue, year, month, viewMode, selectedProductIds, loadBillingInvoices]);

  useEffect(() => {
    if (tabValue !== 1) return;
    if (tenantInfoLoading) return;
    if (Object.keys(tenantInfoById).length > 0) return;
    let cancelled = false;
    (async () => {
      setTenantInfoLoading(true);
      try {
        const res = await apiService.getTenants({
          page: 1,
          limit: 1000,
          ...(selectedSingleProductId ? { productId: selectedSingleProductId } : {})
        });
        if (cancelled || !res.data?.success) return;
        const list = (res.data.data ?? []) as Array<{
          tenantId: string;
          companyName?: string | null;
          logoUrl?: string | null;
          cnpj?: string | null;
        }>;
        const next: Record<
          string,
          { companyName: string | null; logoUrl: string | null; cnpj: string | null }
        > = {};
        for (const t of list) {
          const tid = String(t.tenantId ?? '').trim();
          if (!tid) continue;
          next[tid] = {
            companyName: t.companyName != null ? String(t.companyName) : null,
            logoUrl: (t as any).logoUrl != null ? String((t as any).logoUrl) : null,
            cnpj: t.cnpj != null ? String(t.cnpj) : null
          };
        }
        if (!cancelled) setTenantInfoById(next);
      } catch {
        // sem impacto: tabela continua exibindo tenantId como fallback
      } finally {
        if (!cancelled) setTenantInfoLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tabValue, tenantInfoById, tenantInfoLoading, selectedSingleProductId]);

  /** Garante razão social para cada tenant das faturas billing (lista inicial é limitada / paginada). */
  useEffect(() => {
    if (tabValue !== 1 || billingTenantIds.length === 0) return;
    const missing = billingTenantIds.filter(tid => !tenantInfoById[tid]?.companyName?.trim());
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      setBillingTenantResolveBusy(true);
      try {
        const results = await Promise.all(
          missing.map(async tid => {
            try {
              const res = await apiService.getTenant(
                tid,
                selectedSingleProductId ? { productId: selectedSingleProductId } : undefined
              );
              if (!res.data?.success || !res.data.data) return null;
              const d = res.data.data as {
                tenantId?: string;
                companyName?: string | null;
                logoUrl?: string | null;
                cnpj?: string | null;
              };
              const id = String(d.tenantId ?? tid).trim();
              return {
                tid: id,
                companyName: d.companyName != null ? String(d.companyName) : null,
                logoUrl: d.logoUrl != null ? String(d.logoUrl) : null,
                cnpj: d.cnpj != null ? String(d.cnpj) : null
              };
            } catch {
              return {
                tid,
                companyName: tid,
                logoUrl: null,
                cnpj: null
              };
            }
          })
        );
        if (cancelled) return;
        setTenantInfoById(prev => {
          const merged = { ...prev };
          for (const r of results) {
            if (!r) continue;
            merged[r.tid] = {
              companyName: r.companyName ?? merged[r.tid]?.companyName ?? null,
              logoUrl: r.logoUrl ?? merged[r.tid]?.logoUrl ?? null,
              cnpj: r.cnpj ?? merged[r.tid]?.cnpj ?? null
            };
          }
          return merged;
        });
      } finally {
        if (!cancelled) setBillingTenantResolveBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tabValue, billingTenantIds, tenantInfoById, selectedSingleProductId]);

  useEffect(() => {
    const invoiceId = nfPreviewInvoiceId;
    if (!invoiceId) return;
    if (Object.prototype.hasOwnProperty.call(nfsePayloadByInvoiceIdRef.current, invoiceId)) return;
    let cancelled = false;
    (async () => {
      setNfsePayloadLoadingByInvoiceId(prev => ({ ...prev, [invoiceId]: true }));
      setNfsePayloadErrorByInvoiceId(prev => ({ ...prev, [invoiceId]: null }));
      try {
        const res = await apiService.getBillingInvoiceNfsePayload(invoiceId);
        if (cancelled) return;
        setNfsePayloadByInvoiceId(prev => ({ ...prev, [invoiceId]: res.data ?? null }));
      } catch (e: unknown) {
        if (cancelled) return;
        const ax = e as { response?: { data?: { detail?: string; message?: string } } };
        const msg =
          ax.response?.data?.detail ||
          ax.response?.data?.message ||
          'Prévia do payload não disponível no billing.';
        setNfsePayloadByInvoiceId(prev => ({ ...prev, [invoiceId]: null }));
        setNfsePayloadErrorByInvoiceId(prev => ({ ...prev, [invoiceId]: msg }));
      } finally {
        if (!cancelled) setNfsePayloadLoadingByInvoiceId(prev => ({ ...prev, [invoiceId]: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nfPreviewInvoiceId]);

  useEffect(() => {
    if (!nfPreviewInvoiceId) {
      setNfPreviewBillingCfg(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getBillingGlobalConfig();
        if (!cancelled && res.data) {
          setNfPreviewBillingCfg(res.data as Record<string, unknown>);
        }
      } catch {
        if (!cancelled) setNfPreviewBillingCfg({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nfPreviewInvoiceId]);

  // Safety: ao abrir a prévia, se houver algum "loading" preso, destrava para permitir novo fetch.
  useEffect(() => {
    const invoiceId = nfPreviewInvoiceId;
    if (!invoiceId) return;
    if (nfsePayloadLoadingByInvoiceIdRef.current[invoiceId]) {
      setNfsePayloadLoadingByInvoiceId(prev => ({ ...prev, [invoiceId]: false }));
    }
  }, [nfPreviewInvoiceId]);

  useEffect(() => {
    if (!manualRevenueOpen) return;
    const current = new Date();
    const currentPeriod = toIsoMonth(current.getFullYear(), current.getMonth() + 1);
    setManualRevenueApplicationId(selectedSingleProductId || '');
    setManualRevenueBillingPeriod(currentPeriod);
    setManualRevenueDueDate('');
    setManualRevenueAmountRaw('');
    setManualRevenueStatus('paid');
    const nowDate = new Date();
    const localNow = new Date(nowDate.getTime() - nowDate.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    setManualRevenuePaidAt(localNow);
    setManualRevenueDescription('');
    setManualRevenueNote('');
    setManualRevenueTenantId('');
  }, [manualRevenueOpen, selectedSingleProductId]);

  useEffect(() => {
    if (!manualRevenueOpen) return;
    if (!manualRevenueApplicationId) {
      setManualRevenueTenants([]);
      setManualRevenueDueDayMap({});
      setManualRevenueTenantId('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [tenantsRes, pricingRes] = await Promise.all([
          apiService.getTenants({ page: 1, limit: 500, productId: manualRevenueApplicationId }),
          apiService.getPricingConfig()
        ]);
        if (cancelled || !tenantsRes.data?.success) return;
        setManualRevenueTenants(
          (
            tenantsRes.data.data as Array<{
              tenantId: string;
              companyName: string;
              trialEndsAt?: string;
              subscriptionStartsAt?: string;
              createdAt?: string;
            }>
          ).map(t => ({
            tenantId: t.tenantId,
            companyName: t.companyName,
            trialEndsAt: t.trialEndsAt,
            subscriptionStartsAt: t.subscriptionStartsAt,
            createdAt: t.createdAt
          }))
        );
        const tenantConfigs = pricingRes.data?.data?.tenantConfigs;
        if (Array.isArray(tenantConfigs)) {
          const dueMap: Record<string, number> = {};
          for (const cfg of tenantConfigs) {
            const tid = String(cfg?.tenantId ?? '').trim();
            const dueDay = Number(cfg?.billingDueDay);
            if (tid && Number.isFinite(dueDay) && dueDay >= 1 && dueDay <= 31) {
              dueMap[tid] = dueDay;
            }
          }
          if (!cancelled) setManualRevenueDueDayMap(dueMap);
        }
      } catch {
        if (!cancelled) {
          setManualRevenueTenants([]);
          setManualRevenueDueDayMap({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [manualRevenueOpen, manualRevenueApplicationId]);

  useEffect(() => {
    if (!manualRevenueTenantId) {
      setManualRevenuePeriods([]);
      setManualRevenueBillingPeriod('');
      return;
    }

    const tenant = manualRevenueTenants.find(t => t.tenantId === manualRevenueTenantId);
    const fallbackMin = new Date(new Date().getFullYear(), 0, 1);
    let minDate = fallbackMin;
    if (tenant) {
      const trialEnds = tenant.trialEndsAt ? new Date(tenant.trialEndsAt) : null;
      const startsAt = tenant.subscriptionStartsAt ? new Date(tenant.subscriptionStartsAt) : null;
      const createdAt = tenant.createdAt ? new Date(tenant.createdAt) : null;
      if (trialEnds && !Number.isNaN(trialEnds.getTime())) {
        minDate = trialEnds;
      } else if (startsAt && !Number.isNaN(startsAt.getTime())) {
        const t = new Date(startsAt);
        t.setDate(t.getDate() + 30);
        minDate = t;
      } else if (createdAt && !Number.isNaN(createdAt.getTime())) {
        const t = new Date(createdAt);
        t.setDate(t.getDate() + 30);
        minDate = t;
      }
    }

    const minYear = minDate.getFullYear();
    const minMonth = minDate.getMonth() + 1;
    const nowDate = new Date();
    const maxYear = nowDate.getFullYear() + 1;
    const maxMonth = 12;

    const options: ManualRevenuePeriodOption[] = [];
    let y = minYear;
    let m = minMonth;
    while (y < maxYear || (y === maxYear && m <= maxMonth)) {
      options.push({
        key: toIsoMonth(y, m),
        label: toMonthYearLabel(y, m)
      });
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
    }

    setManualRevenuePeriods(options);
    const current = new Date();
    const currentPeriod = toIsoMonth(current.getFullYear(), current.getMonth() + 1);
    setManualRevenueBillingPeriod(prev => {
      if (options.some(o => o.key === prev)) return prev;
      if (options.some(o => o.key === currentPeriod)) return currentPeriod;
      return options[0]?.key ?? '';
    });
  }, [manualRevenueTenantId, manualRevenueTenants]);

  useEffect(() => {
    if (!manualRevenueBillingPeriod) {
      setManualRevenueDueDate('');
      return;
    }
    const p = parseIsoMonth(manualRevenueBillingPeriod);
    if (!p) return;
    const dueDay = manualRevenueDueDayMap[manualRevenueTenantId] ?? 10;
    const lastDay = new Date(p.year, p.month, 0).getDate();
    const day = Math.min(Math.max(dueDay, 1), lastDay);
    setManualRevenueDueDate(`${p.year}-${pad2(p.month)}-${pad2(day)}`);
  }, [manualRevenueBillingPeriod, manualRevenueTenantId, manualRevenueDueDayMap]);

  useEffect(() => {
    if (!manualRevenueTenantId || !manualRevenueBillingPeriod) {
      setManualRevenueAmountRaw('');
      return;
    }
    const p = parseIsoMonth(manualRevenueBillingPeriod);
    if (!p) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getFinancialRevenues(p.year, p.month);
        if (!res.data?.success || cancelled) return;
        const byTenant = (res.data?.data?.byTenant ?? []) as RevenueByTenant[];
        const row = byTenant.find(t => t.tenantId === manualRevenueTenantId);
        if (cancelled) return;
        if (row && Number.isFinite(row.monthlyAmountBRL)) {
          setManualRevenueAmountRaw(numberToEditableString(row.monthlyAmountBRL));
        } else {
          setManualRevenueAmountRaw('');
        }
      } catch {
        if (!cancelled) setManualRevenueAmountRaw('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [manualRevenueTenantId, manualRevenueBillingPeriod]);

  useEffect(() => {
    if (manualRevenueStatus !== 'paid') return;
    const nowDate = new Date();
    const localNow = new Date(nowDate.getTime() - nowDate.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    setManualRevenuePaidAt(prev => (prev ? prev : localNow));
  }, [manualRevenueStatus]);

  const handleManualRevenueSubmit = async () => {
    const appId = manualRevenueApplicationId.trim();
    if (!appId) {
      showError('Selecione o produto');
      return;
    }
    const tid = manualRevenueTenantId.trim();
    if (!tid) {
      showError('Informe o tenantId');
      return;
    }
    const amt = parseCurrency(manualRevenueAmountRaw);
    if (Number.isNaN(amt) || amt < 0) {
      showError('Informe um valor válido');
      return;
    }
    const due = manualRevenueDueDate.trim();
    if (!due) {
      showError('Informe a data de vencimento');
      return;
    }
    const bp = manualRevenueBillingPeriod.trim();
    if (!bp) {
      showError('Informe o período de cobrança (ex.: 2026-04)');
      return;
    }
    const dueIso = `${due}T12:00:00.000Z`;
    let paidAtIso: string | undefined;
    if (manualRevenueStatus === 'paid' && manualRevenuePaidAt.trim()) {
      const p = new Date(manualRevenuePaidAt);
      if (!Number.isNaN(p.getTime())) paidAtIso = p.toISOString();
    }
    const body = {
      productId: appId,
      tenantId: tid,
      billingPeriod: bp,
      amountBRL: amt,
      dueDate: dueIso,
      status: manualRevenueStatus,
      ...(paidAtIso ? { paidAt: paidAtIso } : {}),
      ...(manualRevenueDescription.trim() ? { description: manualRevenueDescription.trim() } : {}),
      ...(manualRevenueNote.trim() ? { note: manualRevenueNote.trim() } : {})
    };

    setManualRevenueSaving(true);
    try {
      const res = await apiService.createManualBillingInvoice(body);
      if (res.status >= 400) {
        const d = res.data as { detail?: string; message?: string };
        showError(d?.detail || d?.message || 'Erro ao registrar receita');
        return;
      }
      showSuccess('Receita registrada no billing');
      setManualRevenueOpen(false);
      await loadBillingInvoices(false);
      loadData();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: string; message?: string } } };
      showError(
        ax.response?.data?.detail || ax.response?.data?.message || 'Erro ao registrar receita'
      );
    } finally {
      setManualRevenueSaving(false);
    }
  };

  const handleAdjustOpen = (entry: ExpenseEntry) => {
    setAdjustEntry(entry);
    setAdjustAmountRaw(numberToEditableString(entry.amountBRL));
    setAdjustDialogOpen(true);
  };

  const handleAdjustSubmit = async () => {
    if (!adjustEntry) return;
    const amt = parseCurrency(adjustAmountRaw);
    if (amt <= 0) {
      showError('Informe um valor positivo');
      return;
    }
    setAdjusting(true);
    try {
      const res = await apiService.adjustExpenseEntry(adjustEntry._id, amt);
      if (res.data.success) {
        showSuccess('Valor ajustado com sucesso');
        setAdjustDialogOpen(false);
        setAdjustEntry(null);
        loadData();
      } else {
        showError(res.data.message || 'Erro ao ajustar');
      }
    } catch (err) {
      showError('Erro ao ajustar lançamento');
    } finally {
      setAdjusting(false);
    }
  };

  const handleProcessExpensesOpen = () => {
    setSelectedRecurringIds(new Set());
    const amounts: Record<string, string> = {};
    for (const r of recurringExpenses ?? []) {
      if (r.isActive) amounts[r._id] = numberToEditableString(r.defaultAmountBRL);
    }
    setProcessExpenseAmounts(amounts);
    setProcessExpensesModalOpen(true);
  };

  const handleProcessExpensesToggle = (id: string) => {
    setSelectedRecurringIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleProcessExpensesSelectAll = (ids: string[]) => {
    setSelectedRecurringIds(prev => {
      const allSelected = ids.every(id => prev.has(id));
      return allSelected ? new Set<string>() : new Set(ids);
    });
  };

  const handleProcessExpensesAmountChange = (id: string, value: string) => {
    setProcessExpenseAmounts(prev => ({ ...prev, [id]: value }));
  };

  const handleProcessExpensesSubmit = async () => {
    const ids = Array.from(selectedRecurringIds);
    if (ids.length === 0) {
      showError('Selecione pelo menos uma despesa recorrente');
      return;
    }
    const amountOverrides: Record<string, number> = {};
    for (const id of ids) {
      const raw = processExpenseAmounts[id] ?? '';
      const amt = parseCurrency(raw);
      if (amt >= 0) amountOverrides[id] = amt;
    }
    const startMonth = year === now.getFullYear() ? now.getMonth() + 1 : 1;
    setProcessingExpenses(true);
    try {
      const res = await apiService.processExpenseEntries(year, startMonth, ids, amountOverrides);
      if (res.data.success) {
        showSuccess(res.data.message || 'Despesas geradas');
        setProcessExpensesModalOpen(false);
        loadData();
      } else {
        showError(res.data.message || 'Erro ao processar');
      }
    } catch {
      showError('Erro ao gerar despesas');
    } finally {
      setProcessingExpenses(false);
    }
  };

  const handleMarkExpensePaid = (entry: ExpenseEntry) => {
    if (entry.paid) {
      apiService
        .markExpensePaid(entry._id, false)
        .then(res => {
          if (res.data.success) {
            showSuccess('Despesa marcada como não paga');
            loadData();
          } else showError(res.data.message || 'Erro');
        })
        .catch(() => showError('Erro ao atualizar'));
    } else {
      const today = new Date().toISOString().slice(0, 10);
      setPaymentDateValue(today);
      setPaymentDateModal({ entry });
    }
  };

  const handlePaymentDateConfirm = async () => {
    if (!paymentDateModal || !paymentDateValue) return;
    setSubmittingPaymentDate(true);
    try {
      const dateIso = new Date(paymentDateValue + 'T12:00:00').toISOString();
      const res = await apiService.markExpensePaid(paymentDateModal.entry._id, true, dateIso);
      if (res.data.success) {
        showSuccess('Despesa marcada como paga');
        setPaymentDateModal(null);
        loadData();
      } else showError(res.data.message || 'Erro');
    } catch {
      showError('Erro ao atualizar');
    } finally {
      setSubmittingPaymentDate(false);
    }
  };

  const handleDeleteEntry = (entry: ExpenseEntry) => {
    if (entry.source !== 'manual') return;
    confirm({
      title: 'Remover despesa manual?',
      message: 'Esta ação não pode ser desfeita.',
      confirmText: 'Remover',
      confirmColor: 'error',
      onConfirm: async () => {
        const res = await apiService.deleteExpenseEntry(entry._id);
        if (res.data.success) {
          showSuccess('Despesa removida');
          loadData();
        } else {
          showError(res.data.message || 'Erro ao remover');
        }
      }
    });
  };

  const handleExpenseInvoiceMenuOpen = (e: React.MouseEvent<HTMLElement>, entry: ExpenseEntry) => {
    e.stopPropagation();
    setExpenseInvoiceAnchor({ el: e.currentTarget, entry });
  };
  const handleExpenseInvoiceMenuClose = () => setExpenseInvoiceAnchor(null);

  const handleExpenseInvoiceFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const entry = expenseInvoiceAnchor?.entry;
    if (!file || !entry) return;
    setUploadingInvoice(true);
    try {
      const res = await apiService.addExpenseInvoice(entry._id, file, uploadType);
      if (res.data.success) {
        showSuccess(uploadType === 'invoice' ? 'NF anexada' : 'Comprovante anexado');
        handleExpenseInvoiceMenuClose();
        loadData();
      } else {
        showError(res.data.message || 'Erro ao anexar arquivo');
      }
    } catch {
      showError('Erro ao anexar arquivo');
    } finally {
      setUploadingInvoice(false);
      e.target.value = '';
    }
  };

  const handleRemoveExpenseInvoice = async (
    entry: ExpenseEntry,
    key: string,
    type: 'invoice' | 'payment_receipt'
  ) => {
    try {
      const res = await apiService.removeExpenseInvoice(entry._id, key, type);
      if (res.data.success) {
        showSuccess(type === 'invoice' ? 'NF removida' : 'Comprovante removido');
        loadData();
        if (expenseInvoiceAnchor?.entry._id === entry._id) {
          const items =
            type === 'invoice'
              ? (entry.invoices ?? []).filter(i => i.key !== key)
              : (entry.paymentReceipts ?? []).filter(i => i.key !== key);
          if (items.length === 0) handleExpenseInvoiceMenuClose();
        }
      } else {
        showError(res.data.message || 'Erro ao remover arquivo');
      }
    } catch {
      showError('Erro ao remover arquivo');
    }
  };

  const loadRevenueInvoiceDetail = useCallback(
    async (invoiceId: string) => {
      setEditRevenueLoading(true);
      try {
        const res = await apiService.getBillingInvoice(invoiceId);
        const inv = res.data as any;
        setEditRevenueTenantId(String(inv?.tenantId ?? ''));
        const bpRaw = String(inv?.billingPeriod ?? '').trim(); // esperado: YYYY-MM
        const bpMmYyyy = /^\d{4}-\d{2}$/.test(bpRaw)
          ? `${bpRaw.slice(5, 7)}/${bpRaw.slice(0, 4)}`
          : bpRaw;
        setEditRevenueBillingPeriod(bpMmYyyy);
        const due = typeof inv?.dueDate === 'string' ? inv.dueDate.slice(0, 10) : '';
        setEditRevenueDueDate(due);
        const amountCents = Number(inv?.amountCents ?? inv?.amount ?? 0);
        const amountBRL =
          Number.isFinite(amountCents) && amountCents >= 1000
            ? amountCents / 100
            : amountCents / 100;
        setEditRevenueAmountRaw(numberToEditableString(amountBRL || 0));
        setEditRevenueStatus((inv?.status ?? 'paid') as any);
        const paidAt = typeof inv?.paidAt === 'string' ? inv.paidAt : '';
        if (paidAt) {
          const dt = new Date(paidAt);
          const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16);
          setEditRevenuePaidAt(local);
        } else setEditRevenuePaidAt('');
        setEditRevenueDescription(String(inv?.description ?? ''));
        setEditRevenueNote(String(inv?.note ?? ''));
        setEditRevenueMetadata(inv?.metadata ?? {});
      } catch (e: unknown) {
        const ax = e as { response?: { data?: { detail?: string; message?: string } } };
        showError(
          ax.response?.data?.detail || ax.response?.data?.message || 'Erro ao carregar fatura'
        );
      } finally {
        setEditRevenueLoading(false);
      }
    },
    [showError]
  );

  const handleOpenEditRevenue = async (invoiceId: string) => {
    setEditRevenueInvoiceId(invoiceId);
    await loadRevenueInvoiceDetail(invoiceId);
  };

  const handleSaveEditRevenue = async () => {
    if (!editRevenueInvoiceId) return;
    const amt = parseCurrency(editRevenueAmountRaw);
    if (Number.isNaN(amt) || amt < 0) {
      showError('Informe um valor válido');
      return;
    }
    if (!editRevenueDueDate.trim()) {
      showError('Informe a data de vencimento');
      return;
    }
    const dueIso = `${editRevenueDueDate.trim()}T12:00:00.000Z`;
    let paidAtIso: string | undefined;
    if (editRevenueStatus === 'paid' && editRevenuePaidAt.trim()) {
      const p = new Date(editRevenuePaidAt);
      if (!Number.isNaN(p.getTime())) paidAtIso = p.toISOString();
    }
    const bpInput = editRevenueBillingPeriod.trim();
    const billingPeriod = (() => {
      const m = bpInput.match(/^(\d{2})\/(\d{4})$/);
      if (m) return `${m[2]}-${m[1]}`; // YYYY-MM
      if (/^\d{4}-\d{2}$/.test(bpInput)) return bpInput;
      return bpInput;
    })();
    const body: Record<string, unknown> = {
      tenantId: editRevenueTenantId.trim(),
      billingPeriod,
      amountBRL: amt,
      dueDate: dueIso,
      status: editRevenueStatus,
      ...(paidAtIso ? { paidAt: paidAtIso } : { paidAt: null }),
      description: editRevenueDescription.trim() ? editRevenueDescription.trim() : null,
      note: editRevenueNote.trim() ? editRevenueNote.trim() : null,
      metadata: editRevenueMetadata ?? {}
    };

    setEditRevenueSaving(true);
    try {
      const res = await apiService.updateBillingInvoice(editRevenueInvoiceId, body);
      if (res.status >= 400) {
        const d = res.data as { detail?: string; message?: string };
        showError(d?.detail || d?.message || 'Erro ao salvar');
        return;
      }
      showSuccess('Receita atualizada');
      setEditRevenueInvoiceId(null);
      await loadBillingInvoices(false);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: string; message?: string } } };
      showError(ax.response?.data?.detail || ax.response?.data?.message || 'Erro ao salvar');
    } finally {
      setEditRevenueSaving(false);
    }
  };

  const handleRefreshReceipt = async (invoiceId: string) => {
    setRefreshingReceiptByInvoice(prev => ({ ...prev, [invoiceId]: true }));
    try {
      const res = await apiService.refreshBillingInvoiceReceipt(invoiceId);
      if (res.status >= 400) {
        const d = res.data as { detail?: string; message?: string };
        showError(d?.detail || d?.message || 'Erro ao consultar recibo');
        return;
      }
      const data = res.data as {
        paymentConfirmed?: boolean;
        providerStatus?: string;
      };
      if (data?.paymentConfirmed) {
        showSuccess('Pagamento confirmado no Inter — fatura atualizada e NFS-e enfileirada');
      } else {
        showSuccess(
          data?.providerStatus
            ? `Consulta ao Inter concluída (situação: ${data.providerStatus})`
            : 'Consulta enviada ao Inter'
        );
      }
      await loadBillingInvoices(false);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: string; message?: string } } };
      showError(
        ax.response?.data?.detail || ax.response?.data?.message || 'Erro ao consultar recibo'
      );
    } finally {
      setRefreshingReceiptByInvoice(prev => ({ ...prev, [invoiceId]: false }));
    }
  };

  const handleSyncNfseFromIpm = async (invoiceId: string, body?: { rpsNumero?: number }) => {
    setSyncingNfFromIpmByInvoice(prev => ({ ...prev, [invoiceId]: true }));
    try {
      const res = await apiService.syncBillingInvoiceNfseFromIpm(invoiceId, body);
      const data = res.data as {
        success?: boolean;
        synced?: boolean;
        reason?: string;
        detail?: string;
        message?: string;
      };
      if (res.status >= 400) {
        showError(
          data?.reason || data?.detail || data?.message || 'Erro ao consultar NFS-e no IPM'
        );
        return;
      }
      if (data?.success === false) {
        showError(
          data?.reason || data?.detail || data?.message || 'Sincronização com o IPM não concluída'
        );
        return;
      }
      if (data?.synced) {
        showSuccess('NFS-e localizada no IPM; registro atualizado no billing.');
      } else {
        showSuccess('Consulta ao IPM concluída.');
      }
      await loadBillingInvoices(false);
    } catch (e: unknown) {
      const ax = e as {
        response?: { data?: { detail?: string; message?: string; reason?: string } };
      };
      showError(
        ax.response?.data?.reason ||
          ax.response?.data?.detail ||
          ax.response?.data?.message ||
          'Erro ao consultar NFS-e no IPM'
      );
    } finally {
      setSyncingNfFromIpmByInvoice(prev => ({ ...prev, [invoiceId]: false }));
    }
  };

  const handleIssueNfse = async (invoiceId: string, serviceDescription?: string) => {
    type IssueBody = {
      success?: boolean;
      issued?: boolean;
      reason?: string;
      nfse?: { numero?: string; status?: string };
      stages?: {
        provider?: 'ipm' | 'nacional';
        ipm?: {
          accepted?: boolean;
          testMode?: boolean;
          messages?: Array<{ codigo?: string; mensagem?: string; correcao?: string }>;
        };
        persisted?: boolean;
        [k: string]: unknown;
      };
      detail?: string;
      message?: string;
    };
    setNfseIssueResult(null);
    setIssuingNfByInvoice(prev => ({ ...prev, [invoiceId]: true }));
    const startedAt = Date.now();
    const previewMode = (
      nfsePayloadByInvoiceIdRef.current[invoiceId] as { emissionMode?: string } | undefined
    )?.emissionMode;
    setNfseEmissionStatus({
      phase: 'running',
      invoiceId,
      provider: previewMode === 'nacional' ? 'nacional' : 'ipm',
      startedAt,
      messages: []
    });
    let body: IssueBody | undefined;
    let httpStatus: number | undefined;
    let networkError: string | undefined;
    try {
      const res = await apiService.issueBillingInvoiceNfse(
        invoiceId,
        serviceDescription?.trim() ? { serviceDescription: serviceDescription.trim() } : undefined
      );
      body = res.data as IssueBody;
      httpStatus = res.status;
    } catch (e: unknown) {
      const ax = e as {
        response?: { status?: number; data?: IssueBody };
        message?: string;
      };
      body = ax.response?.data;
      httpStatus = ax.response?.status;
      if (!body?.stages && !body?.reason) {
        networkError = ax.message || 'Sem resposta do servidor';
      }
    }

    try {
      const messages = body?.stages?.ipm?.messages ?? [];
      const reason = body?.reason || body?.detail || body?.message;
      if (body?.stages) {
        const authorized = body.success !== false && body.issued === true;
        const pending = body.success !== false && body.issued === false;
        setNfseIssueResult({
          outcome: authorized ? 'success' : pending ? 'pending' : 'rejected',
          testMode: body.stages.ipm?.testMode,
          ipmAccepted: body.stages.ipm?.accepted,
          provider: body.stages.provider === 'nacional' ? 'nacional' : 'ipm',
          persisted: body.stages.persisted,
          numero: body.nfse?.numero,
          reason: authorized ? undefined : reason,
          messages
        });
        setNfseEmissionStatus({
          phase: authorized ? 'success' : pending ? 'pending' : 'rejected',
          invoiceId,
          provider: body.stages.provider === 'nacional' ? 'nacional' : 'ipm',
          startedAt,
          finishedAt: Date.now(),
          testMode: body.stages.ipm?.testMode,
          providerAccepted: body.stages.ipm?.accepted,
          persisted: body.stages.persisted,
          numero: body.nfse?.numero,
          reason: authorized ? undefined : reason,
          messages,
          httpStatus,
          body
        });
        if (authorized) {
          showSuccess(`NFSe emitida${body.nfse?.numero ? ` (nº ${body.nfse.numero})` : ''}`);
        } else if (!pending) {
          showError('NFS-e rejeitada — veja os detalhes no modal');
        }
      } else {
        // Sem etapas: erro de validação do billing (4xx) ou sem resposta (timeout/rede).
        const noResponse = Boolean(networkError) || httpStatus === 503 || httpStatus === 504;
        setNfseIssueResult({
          outcome: noResponse ? 'no-response' : 'rejected',
          reason: noResponse
            ? `Sem resposta do billing${reason ? ` (${reason})` : networkError ? ` (${networkError})` : ''}. A emissão pode ter sido concluída; a lista foi atualizada — confira o status da NFS-e antes de tentar de novo.`
            : reason || 'Erro ao solicitar emissão de NF',
          messages: []
        });
        setNfseEmissionStatus({
          phase: noResponse ? 'no-response' : 'rejected',
          invoiceId,
          provider: previewMode === 'nacional' ? 'nacional' : 'ipm',
          startedAt,
          finishedAt: Date.now(),
          reason: noResponse
            ? `Sem resposta do billing${reason ? ` (${reason})` : networkError ? ` (${networkError})` : ''}`
            : reason || 'Erro ao solicitar emissão de NF',
          messages: [],
          httpStatus,
          body: body ?? { networkError }
        });
        showError(
          noResponse
            ? 'Sem resposta do billing durante a emissão'
            : reason || 'Erro ao solicitar emissão de NF'
        );
      }
      await loadBillingInvoices(false);
    } finally {
      setIssuingNfByInvoice(prev => ({ ...prev, [invoiceId]: false }));
    }
  };

  const handleOpenInvoicePreview = async (key: string) => {
    try {
      const url = await apiService.getInvoicePreviewUrl(key);
      if (url) window.open(url, '_blank');
      else showError('URL não disponível');
    } catch {
      showError('Erro ao abrir preview');
    }
  };

  const handleOpenInterReceipt = (row: BillingInvoiceRow) => {
    if (row.paymentProofUrl?.trim()) {
      window.open(row.paymentProofUrl.trim(), '_blank', 'noopener,noreferrer');
      return;
    }
    if (row.paymentId?.trim()) {
      window.open(
        `/billing/payments/${encodeURIComponent(row.paymentId.trim())}/receipt`,
        '_blank'
      );
      return;
    }
    showError('Recibo indisponível (sem pagamento associado).');
  };

  const handleManualOpen = () => {
    setManualDesc('');
    setManualAmountRaw('');
    setManualDay(1);
    setManualMonth(month);
    setManualYear(year);
    setManualRecurringId('');
    setManualDialogOpen(true);
  };

  const handleManualSubmit = async () => {
    const amt = parseCurrency(manualAmountRaw);
    const recurring = manualRecurringId.trim()
      ? recurringExpenses.find(r => r._id === manualRecurringId)
      : null;
    const description = recurring ? recurring.description : manualDesc.trim();
    if (amt <= 0) {
      showError('Informe valor positivo');
      return;
    }
    if (!description) {
      showError('Informe descrição ou selecione uma despesa recorrente');
      return;
    }
    if (manualDay < 1 || manualDay > 31) {
      showError('Dia deve ser entre 1 e 31');
      return;
    }
    setCreatingManual(true);
    try {
      const res = await apiService.createManualExpense(manualYear, manualMonth, {
        description,
        amountBRL: amt,
        day: manualDay,
        ...(manualRecurringId.trim() && { recurringExpenseId: manualRecurringId.trim() })
      });
      if (res.data.success) {
        showSuccess('Despesa adicionada');
        setManualDialogOpen(false);
        loadData();
      } else {
        showError(res.data.message || 'Erro ao criar');
      }
    } catch {
      showError('Erro ao criar despesa');
    } finally {
      setCreatingManual(false);
    }
  };

  const handleRecurringOpen = (edit?: RecurringExpense) => {
    if (edit) {
      setRecurringEdit(edit);
      setRecurringDesc(edit.description);
      setRecurringAmountRaw(numberToEditableString(edit.defaultAmountBRL));
      setRecurringDay(edit.dueDayOfMonth);
      setRecurringPeriod(edit.recurrencePeriod ?? 'monthly');
      setRecurringStartDate(recurrenceStartDateFromExpense(edit));
    } else {
      setRecurringEdit(null);
      setRecurringDesc('');
      setRecurringAmountRaw('');
      setRecurringDay(1);
      setRecurringPeriod('monthly');
      setRecurringStartDate(defaultRecurrenceStartDateValue());
    }
    setRecurringDialogOpen(true);
  };

  const handleRecurringSubmit = async () => {
    const amt = parseCurrency(recurringAmountRaw);
    if (amt < 0 || !recurringDesc.trim()) {
      showError('Informe descrição e valor não negativo');
      return;
    }
    if (recurringDay < 1 || recurringDay > 31) {
      showError('Dia de vencimento deve ser entre 1 e 31');
      return;
    }
    if (!recurringStartDate) {
      showError('Informe a data de início da recorrência');
      return;
    }
    setSavingRecurring(true);
    try {
      const recurrencePayload = {
        recurrencePeriod: recurringPeriod,
        recurrenceStartDate: recurringStartDate
      };
      if (recurringEdit) {
        const res = await apiService.updateRecurringExpense(recurringEdit._id, {
          description: recurringDesc.trim(),
          defaultAmountBRL: amt,
          dueDayOfMonth: recurringDay,
          ...recurrencePayload
        });
        if (res.data.success) {
          showSuccess('Despesa recorrente atualizada');
          setRecurringDialogOpen(false);
          loadData();
        } else {
          showError(res.data.message || 'Erro ao atualizar');
        }
      } else {
        const res = await apiService.createRecurringExpense({
          description: recurringDesc.trim(),
          defaultAmountBRL: amt,
          dueDayOfMonth: recurringDay,
          ...recurrencePayload
        });
        if (res.data.success) {
          showSuccess('Despesa recorrente criada');
          setRecurringDialogOpen(false);
          loadData();
        } else {
          showError(res.data.message || 'Erro ao criar');
        }
      }
    } catch {
      showError('Erro ao salvar despesa recorrente');
    } finally {
      setSavingRecurring(false);
    }
  };

  const handleDeleteRecurring = (r: RecurringExpense) => {
    confirm({
      title: 'Remover despesa recorrente?',
      message: 'Isso não remove lançamentos já gerados, apenas deixa de criar novos.',
      confirmText: 'Remover',
      confirmColor: 'error',
      onConfirm: async () => {
        const res = await apiService.deleteRecurringExpense(r._id);
        if (res.data.success) {
          showSuccess('Despesa recorrente removida');
          loadData();
        } else {
          showError(res.data.message || 'Erro ao remover');
        }
      }
    });
  };

  const handleToggleRecurringActive = async (r: RecurringExpense) => {
    try {
      const res = await apiService.updateRecurringExpense(r._id, { isActive: !r.isActive });
      if (res.data.success) {
        showSuccess(r.isActive ? 'Despesa desativada' : 'Despesa ativada');
        loadData();
      }
    } catch {
      showError('Erro ao alterar status');
    }
  };

  const years = Array.from({ length: 6 }, (_, i) => now.getFullYear() - 2 + i);

  const periodLabel = useMemo(() => {
    if (viewMode === 'month') {
      return `${MONTH_NAMES[month - 1]} de ${year}`;
    }
    return `Ano calendário ${year}`;
  }, [viewMode, month, year]);
  const periodEstimatedRevenue = summary?.totalRevenues ?? 0;
  const periodReceivedRevenue = summary?.totalRevenuesReceived ?? 0;
  const periodRevenueDeviation = periodReceivedRevenue - periodEstimatedRevenue;
  const periodRevenueAchievementPct =
    periodEstimatedRevenue > 0 ? (periodReceivedRevenue / periodEstimatedRevenue) * 100 : 0;

  const dashboardErrorPanel = dashboardLoadError
    ? errorToPanel(parseLoadError(dashboardLoadError))
    : null;

  return (
    <Box component="div">
      <>
        <Card variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="h5">Fluxo Financeiro</Typography>
              <Typography variant="body2" color="text.secondary">
                Visão executiva entre despesas da empresa, receitas realizadas no billing e MRR
                estimado pela precificação. Use as abas para separar realizado, previsto e
                recorrência.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  color="primary"
                  label={
                    selectedProductIds.length
                      ? `${selectedProductIds.length} produto(s) selecionado(s)`
                      : 'Todos os produtos permitidos'
                  }
                />
                <Chip size="small" variant="outlined" label="Realizado: vover-billing" />
                <Chip size="small" variant="outlined" label="Estimado: pricing_config" />
                <Chip size="small" variant="outlined" label={`Período: ${periodLabel}`} />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {loading && !dashboardLoadError && <ProductScopeStatePanel variant="checking" title="" />}

        {dashboardErrorPanel && (
          <ProductScopeStatePanel
            variant="blocked"
            title={dashboardErrorPanel.title}
            detail={dashboardErrorPanel.detail ?? ''}
            severity={dashboardErrorPanel.severity ?? 'error'}
            onRetry={loadData}
          />
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Visão</InputLabel>
                <Select
                  value={viewMode}
                  onChange={e => setViewMode(e.target.value as 'month' | 'year')}
                  label="Visão"
                >
                  <MenuItem value="month">Por mês</MenuItem>
                  <MenuItem value="year">Por ano</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {viewMode === 'month' && (
              <Grid item>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Mês</InputLabel>
                  <Select
                    value={month}
                    onChange={e => setMonth(Number(e.target.value))}
                    label="Mês"
                  >
                    {MONTH_NAMES.map((name, i) => (
                      <MenuItem key={i} value={i + 1}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Ano</InputLabel>
                <Select value={year} onChange={e => setYear(Number(e.target.value))} label="Ano">
                  {years.map(y => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Stack spacing={3} sx={{ mb: 3 }}>
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1.5 }}
                  flexWrap="wrap"
                >
                  <Typography variant="overline" color="text.secondary" letterSpacing={1}>
                    No período selecionado
                  </Typography>
                  <Chip size="small" label={periodLabel} variant="outlined" color="primary" />
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ height: '100%', minHeight: 148 }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                          <TrendingDown color="error" />
                          <Typography color="text.secondary" variant="body2">
                            Despesas pagas no período
                          </Typography>
                        </Box>
                        <Typography variant="h5" component="p">
                          {formatCurrency(summary?.totalExpensesPaid ?? 0)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          Inclui só lançamentos do período · Total (incl. em aberto):{' '}
                          {formatCurrency(summary?.totalExpenses ?? 0)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ height: '100%', minHeight: 148 }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                          <TrendingUp color="success" />
                          <Typography color="text.secondary" variant="body2">
                            Faturas pagas no período
                          </Typography>
                          <Tooltip title="Soma de faturas com status pago cuja data de vencimento cai no período (vover-billing).">
                            <InfoOutlined
                              sx={{ fontSize: 18, color: 'action.active', cursor: 'help' }}
                            />
                          </Tooltip>
                        </Box>
                        <Typography variant="h5" component="p">
                          {formatCurrency(summary?.totalRevenuesReceived ?? 0)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          MRR estimado (pricing): {formatCurrency(periodEstimatedRevenue)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.25 }}
                        >
                          Desvio (realizado - estimado): {formatCurrency(periodRevenueDeviation)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.25 }}
                        >
                          % atingido: {periodRevenueAchievementPct.toFixed(1)}%
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ height: '100%', minHeight: 148 }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                          <AccountBalance color="primary" />
                          <Typography color="text.secondary" variant="body2">
                            Saldo no período
                          </Typography>
                        </Box>
                        <Typography
                          variant="h5"
                          component="p"
                          color={(summary?.balance ?? 0) >= 0 ? 'success.main' : 'error.main'}
                        >
                          {formatCurrency(summary?.balance ?? 0)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          Receitas realizadas (billing) − despesas pagas no período
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>

              <Paper variant="outlined" sx={{ p: 2, bgcolor: theme => theme.palette.grey[50] }}>
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  spacing={1}
                  sx={{ mb: 2 }}
                  flexWrap="wrap"
                >
                  <Box>
                    <Typography variant="overline" color="text.secondary" letterSpacing={1}>
                      Acumulado histórico
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Visão consolidada desde o início dos registros — independente dos filtros
                      acima.
                    </Typography>
                  </Box>
                  <Tooltip
                    title={
                      'Despesas: soma de todos os lançamentos marcados como pagos no admin. Receitas: soma de todas as faturas pagas no billing (vencimento até hoje). Útil para ver se a operação está no azul no agregado.'
                    }
                  >
                    <IconButton size="small" aria-label="Sobre totais históricos" sx={{ mt: -0.5 }}>
                      <InfoOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%', minHeight: 148 }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                          <TrendingDown color="error" />
                          <Typography color="text.secondary" variant="body2">
                            Total despesas pagas
                          </Typography>
                        </Box>
                        <Typography variant="h5" component="p">
                          {formatCurrency(lifetime?.totalExpensesPaid ?? 0)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          Todos os meses · base admin (core)
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%', minHeight: 148 }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                          <TrendingUp color="success" />
                          <Typography color="text.secondary" variant="body2">
                            Total faturas pagas (billing)
                          </Typography>
                        </Box>
                        <Typography variant="h5" component="p">
                          {formatCurrency(lifetime?.totalRevenuesReceived ?? 0)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          vover-billing · faturas com status pago
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%', minHeight: 148 }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                          <AccountBalance color="primary" />
                          <Typography color="text.secondary" variant="body2">
                            Saldo acumulado
                          </Typography>
                        </Box>
                        <Typography
                          variant="h5"
                          component="p"
                          color={(lifetime?.balance ?? 0) >= 0 ? 'success.main' : 'error.main'}
                        >
                          {formatCurrency(lifetime?.balance ?? 0)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          Receitas realizadas (histórico) − despesas pagas (histórico)
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
            </Stack>

            <Paper>
              <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
                <Tab icon={<Receipt />} iconPosition="start" label="Despesas" />
                <Tab icon={<AttachMoney />} iconPosition="start" label="RECEITAS" />
                <Tab icon={<TrendingUp />} iconPosition="start" label="MRR estimado (pricing)" />
                <Tab icon={<Assessment />} iconPosition="start" label="Relatório anual" />
                <Tab icon={<Repeat />} iconPosition="start" label="Despesas recorrentes" />
              </Tabs>

              {tabValue === 0 && (
                <Box p={2}>
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    gap={2}
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                      Lançamentos de despesas {viewMode === 'year' ? '(ano)' : ''}
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleManualOpen}
                        size="small"
                      >
                        Adicionar despesa manual
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleProcessExpensesOpen}
                        disabled={loading || !recurringExpenses?.length}
                        size="small"
                      >
                        Gerar despesas do ano
                      </Button>
                    </Box>
                  </Box>
                  {expenses.some(isExpenseOverdue) && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {expenses.filter(isExpenseOverdue).length} despesa(s) em atraso — referência{' '}
                      {expenses
                        .filter(isExpenseOverdue)
                        .map(e => formatMonthYearRef(e.month ?? month, e.year ?? year))
                        .join(', ')}
                    </Alert>
                  )}
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Mês/Ano ref.</TableCell>
                          {viewMode === 'year' && <TableCell>Mês</TableCell>}
                          <TableCell>Status</TableCell>
                          <TableCell>Descrição</TableCell>
                          <TableCell>Dia venc.</TableCell>
                          <TableCell align="right">Valor (R$)</TableCell>
                          <TableCell>Pago</TableCell>
                          <TableCell>Tipo</TableCell>
                          <TableCell padding="none">Anexos</TableCell>
                          <TableCell align="right">Ações</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {expenses.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={viewMode === 'year' ? 10 : 9}
                              align="center"
                              sx={{ py: 3 }}
                            >
                              {viewMode === 'year'
                                ? 'Nenhuma despesa neste ano.'
                                : 'Nenhuma despesa neste mês.'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          expenses.map(entry => {
                            const overdue = isExpenseOverdue(entry);
                            const dueDate = getExpenseDueDate(entry);
                            const dueStr = dueDate
                              ? `${String(dueDate.getDate()).padStart(2, '0')}/${String(dueDate.getMonth() + 1).padStart(2, '0')}/${dueDate.getFullYear()}`
                              : '';
                            return (
                              <TableRow
                                key={entry._id}
                                sx={overdue ? { bgcolor: 'rgba(211, 47, 47, 0.06)' } : {}}
                              >
                                <TableCell>
                                  <Tooltip
                                    title={dueStr ? `Vencimento: ${dueStr}` : ''}
                                    arrow
                                    placement="top"
                                  >
                                    <span>
                                      {formatMonthYearRef(entry.month ?? month, entry.year ?? year)}
                                    </span>
                                  </Tooltip>
                                </TableCell>
                                {viewMode === 'year' && (
                                  <TableCell>
                                    {entry.month ? MONTH_NAMES[entry.month - 1] : '-'}
                                  </TableCell>
                                )}
                                <TableCell>
                                  {entry.paid ? (
                                    <Chip
                                      label="Pago"
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                    />
                                  ) : overdue ? (
                                    <Chip label="Em atraso" size="small" color="error" />
                                  ) : (
                                    <Chip label="Em dia" size="small" variant="outlined" />
                                  )}
                                </TableCell>
                                <TableCell>{entry.description}</TableCell>
                                <TableCell>{entry.dueDayOfMonth ?? '-'}</TableCell>
                                <TableCell align="right">
                                  {formatCurrency(entry.amountBRL)}
                                  {entry.wasAdjusted && (
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      color="success.main"
                                      sx={{ ml: 0.5 }}
                                    >
                                      (ajustado)
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    title={entry.paid ? 'Marcar como não pago' : 'Marcar como pago'}
                                    onClick={() => handleMarkExpensePaid(entry)}
                                  >
                                    {entry.paid ? (
                                      <CheckCircle color="success" fontSize="small" />
                                    ) : (
                                      <RadioButtonUnchecked fontSize="small" />
                                    )}
                                  </IconButton>
                                </TableCell>
                                <TableCell>
                                  {entry.source === 'recurring' ? 'Recorrente' : 'Manual'}
                                </TableCell>
                                <TableCell padding="none">
                                  <IconButton
                                    size="small"
                                    title="Anexar NF"
                                    onClick={e => handleExpenseInvoiceMenuOpen(e, entry)}
                                    color={
                                      (entry.invoices && entry.invoices.length > 0) ||
                                      (entry.paymentReceipts && entry.paymentReceipts.length > 0)
                                        ? 'primary'
                                        : 'default'
                                    }
                                  >
                                    <AttachFile fontSize="small" />
                                  </IconButton>
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton
                                    size="small"
                                    title="Ajustar valor real"
                                    onClick={() => handleAdjustOpen(entry)}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                  {entry.source === 'manual' && (
                                    <IconButton
                                      size="small"
                                      color="error"
                                      title="Remover"
                                      onClick={() => handleDeleteEntry(entry)}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <input
                    ref={expenseFileInputRef}
                    type="file"
                    accept=".pdf,image/*"
                    style={{ display: 'none' }}
                    onChange={handleExpenseInvoiceFileChange}
                  />
                  <Menu
                    anchorEl={expenseInvoiceAnchor?.el ?? null}
                    open={Boolean(expenseInvoiceAnchor)}
                    onClose={handleExpenseInvoiceMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    {(expenseInvoiceAnchor?.entry.invoices ?? []).map(inv => (
                      <MenuItem key={inv.key} onClick={() => handleOpenInvoicePreview(inv.key)}>
                        <ListItemIcon>
                          <InsertDriveFile fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={inv.fileName}
                          secondary={new Date(inv.uploadedAt).toLocaleDateString()}
                        />
                        <IconButton
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            handleRemoveExpenseInvoice(
                              expenseInvoiceAnchor!.entry,
                              inv.key,
                              'invoice'
                            );
                          }}
                          title="Remover"
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </MenuItem>
                    ))}
                    {(expenseInvoiceAnchor?.entry.paymentReceipts ?? []).map(inv => (
                      <MenuItem key={inv.key} onClick={() => handleOpenInvoicePreview(inv.key)}>
                        <ListItemIcon>
                          <InsertDriveFile fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Comprovante: ${inv.fileName}`}
                          secondary={new Date(inv.uploadedAt).toLocaleDateString()}
                        />
                        <IconButton
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            handleRemoveExpenseInvoice(
                              expenseInvoiceAnchor!.entry,
                              inv.key,
                              'payment_receipt'
                            );
                          }}
                          title="Remover"
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </MenuItem>
                    ))}
                    <MenuItem
                      onClick={() => {
                        setUploadType('invoice');
                        expenseFileInputRef.current?.click();
                      }}
                      disabled={uploadingInvoice}
                    >
                      <ListItemIcon>
                        <AttachFile fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={uploadingInvoice ? 'Enviando...' : 'Anexar NF'} />
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setUploadType('payment_receipt');
                        expenseFileInputRef.current?.click();
                      }}
                      disabled={uploadingInvoice}
                    >
                      <ListItemIcon>
                        <AttachFile fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={uploadingInvoice ? 'Enviando...' : 'Anexar comprovante'}
                      />
                    </MenuItem>
                  </Menu>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Use &quot;Processar despesas do mês&quot; para gerar lançamentos a partir das
                    despesas recorrentes. Ajuste o valor quando receber a cobrança real (cotação do
                    dia).
                  </Alert>
                </Box>
              )}

              {tabValue === 1 && (
                <Box p={2}>
                  {(() => {
                    const b = periodBounds(year, month, viewMode);
                    const fmt = (iso: string) => {
                      const d = new Date(`${iso}T12:00:00`);
                      return Number.isNaN(d.getTime())
                        ? iso
                        : d.toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          });
                    };
                    const fromLabel = fmt(b.from);
                    const toLabel = fmt(b.to);
                    return (
                      <Box
                        display="flex"
                        flexWrap="wrap"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        gap={2}
                        sx={{ mb: 2 }}
                      >
                        <Box>
                          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                            Receita realizada (vover-billing)
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Faturas no período {fromLabel} — {toLabel} (filtro por data de
                            vencimento). NF-e e pagamentos vêm do mesmo serviço que o app do
                            cliente.
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Add />}
                          onClick={() => setManualRevenueOpen(true)}
                        >
                          Registrar receita manual
                        </Button>
                      </Box>
                    );
                  })()}
                  {billingListError && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {billingListError}
                    </Alert>
                  )}
                  {billingListLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Produto</TableCell>
                              <TableCell>Cliente</TableCell>
                              <TableCell>Período cobrança</TableCell>
                              <TableCell>Vencimento</TableCell>
                              <TableCell align="right">Valor (R$)</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Pago em</TableCell>
                              <TableCell>Recibo (Inter)</TableCell>
                              <TableCell>Anexos</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {billingInvoices.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                  Nenhuma fatura no período ou billing indisponível.
                                </TableCell>
                              </TableRow>
                            ) : (
                              billingInvoices.map(row => (
                                <TableRow key={row.id}>
                                  <TableCell>
                                    {(() => {
                                      const appId = row.productId || selectedSingleProductId;
                                      return appId ? (
                                        <ProductCell
                                          productId={appId}
                                          name={productsById.get(appId)?.displayName}
                                          avatarUrl={productsById.get(appId)?.avatarUrl}
                                        />
                                      ) : (
                                        '—'
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell>
                                    {(() => {
                                      const cn = tenantInfoById[row.tenantId]?.companyName?.trim();
                                      return (
                                        <Box>
                                          <ClientCell
                                            name={cn}
                                            tenantId={row.tenantId}
                                            size="small"
                                          />
                                          {!cn &&
                                          (tenantInfoLoading || billingTenantResolveBusy) ? (
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              display="block"
                                            >
                                              Carregando nome…
                                            </Typography>
                                          ) : null}
                                        </Box>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell>
                                    {(() => {
                                      const bp = String(row.billingPeriod ?? '').trim(); // YYYY-MM
                                      if (/^\d{4}-\d{2}$/.test(bp))
                                        return `${bp.slice(5, 7)}/${bp.slice(0, 4)}`;
                                      return bp || '—';
                                    })()}
                                  </TableCell>
                                  <TableCell>
                                    {row.dueDate
                                      ? new Date(row.dueDate).toLocaleDateString('pt-BR')
                                      : '—'}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency((row.amountCents ?? 0) / 100)}
                                  </TableCell>
                                  <TableCell>
                                    <Chip size="small" label={row.status} variant="outlined" />
                                  </TableCell>
                                  <TableCell>
                                    {row.paidAt
                                      ? new Date(row.paidAt).toLocaleString('pt-BR')
                                      : '—'}
                                  </TableCell>
                                  <TableCell>
                                    <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                                      <Button
                                        size="small"
                                        variant={row.paymentProofUrl ? 'contained' : 'outlined'}
                                        onClick={() => handleOpenInterReceipt(row)}
                                        disabled={!row.paymentId && !row.paymentProofUrl}
                                      >
                                        {row.paymentProofUrl
                                          ? 'Abrir'
                                          : row.paymentId
                                            ? 'Ver'
                                            : '—'}
                                      </Button>
                                      {!row.paymentProofUrl && row.paymentId && (
                                        <Button
                                          size="small"
                                          variant="text"
                                          disabled={Boolean(refreshingReceiptByInvoice[row.id])}
                                          onClick={() => void handleRefreshReceipt(row.id)}
                                        >
                                          {refreshingReceiptByInvoice[row.id]
                                            ? 'Consultando…'
                                            : 'Consultar'}
                                        </Button>
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                                      {row.nfse?.pdfUrl ? (
                                        <Button
                                          size="small"
                                          href={row.nfse.pdfUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          startIcon={<PictureAsPdf fontSize="small" />}
                                        >
                                          PDF
                                        </Button>
                                      ) : null}
                                      {row.status === 'paid' &&
                                      (!row.nfse?.pdfUrl || row.nfse.status === 'rejected') ? (
                                        <Box
                                          display="flex"
                                          gap={0.5}
                                          flexWrap="wrap"
                                          alignItems="center"
                                        >
                                          {!row.nfse?.pdfUrl && (
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              onClick={() => setNfPreviewInvoiceId(row.id)}
                                            >
                                              Emitir NF
                                            </Button>
                                          )}
                                          <Button
                                            size="small"
                                            variant="text"
                                            disabled={Boolean(syncingNfFromIpmByInvoice[row.id])}
                                            onClick={() => {
                                              const invId = String(row.id ?? '').trim();
                                              if (!invId) {
                                                showError(
                                                  'ID da fatura inválido na listagem. Recarregue a página ou a lista de receitas.'
                                                );
                                                return;
                                              }
                                              setNfPreviewInvoiceId(null);
                                              setIpmSyncDialogInvoiceId(invId);
                                              setIpmSyncRpsInput('');
                                            }}
                                          >
                                            {syncingNfFromIpmByInvoice[row.id]
                                              ? 'Consultando IPM…'
                                              : 'Consultar no IPM'}
                                          </Button>
                                        </Box>
                                      ) : !row.nfse?.pdfUrl ? (
                                        '—'
                                      ) : null}
                                      <Tooltip title="Editar receita (fatura billing)">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleOpenEditRevenue(row.id)}
                                        >
                                          <Edit fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {billingNextCursor && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                          <Button
                            variant="outlined"
                            disabled={billingLoadingMore}
                            onClick={() =>
                              loadBillingInvoices(true, billingNextCursor ?? undefined)
                            }
                          >
                            {billingLoadingMore ? <CircularProgress size={22} /> : 'Carregar mais'}
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}

              {tabValue === 2 && (
                <Box p={2}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    spacing={1}
                    sx={{ mb: 1 }}
                  >
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="subtitle1">MRR estimado (pricing_config)</Typography>
                        {revenues?.isClosed ? (
                          <Tooltip
                            title={
                              <Box>
                                <div>
                                  Fechado em{' '}
                                  {revenues.closedAt
                                    ? new Date(revenues.closedAt).toLocaleString('pt-BR')
                                    : '-'}
                                </div>
                                <div>Origem: {revenues.source}</div>
                                {revenues.reopenCount && revenues.reopenCount > 0 ? (
                                  <div>Reaberturas: {revenues.reopenCount}</div>
                                ) : null}
                                {revenues.notes ? <div>Notas: {revenues.notes}</div> : null}
                              </Box>
                            }
                          >
                            <Chip
                              size="small"
                              color="success"
                              icon={<Lock sx={{ fontSize: 16 }} />}
                              label={`Fechado · ${revenues.competence}`}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Competência ainda não fechada — valor é calculado em tempo real e muda quando há novos clientes ou ajustes de preço.">
                            <Chip
                              size="small"
                              color="warning"
                              icon={<LockOpen sx={{ fontSize: 16 }} />}
                              label={`Em aberto · ${revenues?.competence ?? '—'}`}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        Projeção mensal a partir de planos e tenantConfigs. Quando uma competência
                        fecha, o valor passa a ser histórico e imutável.
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {revenues?.isClosed ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<Refresh />}
                          disabled={reopenSubmitting}
                          onClick={handleReopenCompetence}
                        >
                          {reopenSubmitting ? 'Recalculando…' : 'Reabrir / Recalcular'}
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<Lock />}
                          disabled={closeSubmitting}
                          onClick={handleCloseCompetence}
                        >
                          {closeSubmitting ? 'Fechando…' : 'Fechar competência'}
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={handleOpenSeed}
                      >
                        Semear histórico
                      </Button>
                    </Stack>
                  </Stack>
                  <Box mb={2}>
                    <Typography variant="body1">
                      Total estimado {viewMode === 'year' ? '(ano)' : '(mês)'}:{' '}
                      <strong>
                        {formatCurrency(
                          viewMode === 'year'
                            ? (revenues?.totalMonthlyBRL ?? 0) * 12
                            : (revenues?.totalMonthlyBRL ?? 0)
                        )}
                      </strong>
                    </Typography>
                  </Box>
                  {(() => {
                    const allTenants = revenues?.byTenant ?? [];
                    const colSpan = isMultiProduct ? 5 : 4;

                    // Quando multi-produto: agrupa por produto, ordenado por nome do produto → empresa
                    const sortedTenants = [...allTenants].sort((a, b) => {
                      if (isMultiProduct) {
                        const pa = productsById.get(a.productId ?? '')?.displayName ?? '';
                        const pb = productsById.get(b.productId ?? '')?.displayName ?? '';
                        const cmp = pa.localeCompare(pb, 'pt-BR');
                        if (cmp !== 0) return cmp;
                      }
                      return a.companyName.localeCompare(b.companyName, 'pt-BR');
                    });

                    // Agrupa para subtotais por produto
                    const productGroups: Array<{
                      productId: string;
                      tenants: typeof sortedTenants;
                    }> = [];
                    if (isMultiProduct) {
                      for (const t of sortedTenants) {
                        const pid = t.productId ?? '';
                        const last = productGroups[productGroups.length - 1];
                        if (!last || last.productId !== pid) {
                          productGroups.push({ productId: pid, tenants: [t] });
                        } else {
                          last.tenants.push(t);
                        }
                      }
                    }

                    const renderTenantRow = (t: RevenueByTenant) => {
                      const b = t.breakdown;
                      const tenantEstimatedAmount =
                        viewMode === 'year' ? t.monthlyAmountBRL * 12 : t.monthlyAmountBRL;
                      const isCondo =
                        t.packageType === 'condominio' &&
                        b &&
                        (b.basePrice > 0 ||
                          b.extraUsersCost > 0 ||
                          (b.perClientBreakdown && b.perClientBreakdown.length > 0));
                      const expanded = expandedProjectedTenants.has(t.tenantId);
                      const basePlusPackage = (b?.basePrice ?? 0) + (b?.extraUsersCost ?? 0);
                      return (
                        <React.Fragment key={t.tenantId}>
                          <TableRow>
                            <TableCell>
                              {isCondo ? (
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setExpandedProjectedTenants(prev => {
                                      const next = new Set(prev);
                                      if (next.has(t.tenantId)) next.delete(t.tenantId);
                                      else next.add(t.tenantId);
                                      return next;
                                    })
                                  }
                                >
                                  {expanded ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                              ) : null}
                            </TableCell>
                            {isMultiProduct && (
                              <TableCell>
                                {t.productId ? (
                                  <ProductCell
                                    productId={t.productId}
                                    name={productsById.get(t.productId)?.displayName}
                                    avatarUrl={productsById.get(t.productId)?.avatarUrl}
                                  />
                                ) : (
                                  '—'
                                )}
                              </TableCell>
                            )}
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <ClientCell
                                  name={t.companyName}
                                  tenantId={t.tenantId}
                                  size="small"
                                />
                                {t.lockSource === 'invoice-paid' ? (
                                  <Tooltip
                                    title={
                                      <Box>
                                        <div>
                                          Valor trancado — fatura desta competência já está paga.
                                        </div>
                                        {t.lockedAt ? (
                                          <div>
                                            Pago em {new Date(t.lockedAt).toLocaleString('pt-BR')}
                                          </div>
                                        ) : null}
                                        {t.lockedByInvoiceIds && t.lockedByInvoiceIds.length > 0 ? (
                                          <div>Invoice(s): {t.lockedByInvoiceIds.join(', ')}</div>
                                        ) : null}
                                        <div>
                                          Mudanças de preço só afetam a próxima competência.
                                        </div>
                                      </Box>
                                    }
                                  >
                                    <Chip
                                      size="small"
                                      color="success"
                                      icon={<Lock sx={{ fontSize: 14 }} />}
                                      label="Pago · trancado"
                                    />
                                  </Tooltip>
                                ) : null}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              {t.packageType === 'condominio'
                                ? 'Condomínio'
                                : t.packageType === 'institucional'
                                  ? 'Institucional'
                                  : 'Operador'}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(tenantEstimatedAmount)}
                            </TableCell>
                          </TableRow>
                          {isCondo && (
                            <TableRow>
                              <TableCell colSpan={colSpan} sx={{ py: 0, borderBottom: 0 }}>
                                <Collapse in={expanded} timeout="auto" unmountOnExit>
                                  <Table size="small" sx={{ my: 1 }}>
                                    <TableBody>
                                      {basePlusPackage > 0 && (
                                        <TableRow>
                                          <TableCell>—</TableCell>
                                          <TableCell>Base + pacote</TableCell>
                                          <TableCell align="right">
                                            {formatCurrency(basePlusPackage)}
                                          </TableCell>
                                          <TableCell />
                                        </TableRow>
                                      )}
                                      {(b?.perClientBreakdown ?? []).map((pc, i) => (
                                        <TableRow key={i}>
                                          <TableCell>—</TableCell>
                                          <TableCell>{pc.clientName}</TableCell>
                                          <TableCell align="right">
                                            {formatCurrency(pc.total)}
                                          </TableCell>
                                          <TableCell />
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    };

                    return (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell width={40} />
                              {isMultiProduct && <TableCell>Produto</TableCell>}
                              <TableCell>Cliente</TableCell>
                              <TableCell>Pacote</TableCell>
                              <TableCell align="right">
                                {viewMode === 'year' ? 'Total estimado (R$)' : 'MRR estimado (R$)'}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedTenants.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={colSpan} align="center" sx={{ py: 3 }}>
                                  Sem dados de pricing para o período.
                                </TableCell>
                              </TableRow>
                            ) : isMultiProduct ? (
                              productGroups.map(group => {
                                const subtotal = group.tenants.reduce(
                                  (s, t) =>
                                    s +
                                    (viewMode === 'year'
                                      ? t.monthlyAmountBRL * 12
                                      : t.monthlyAmountBRL),
                                  0
                                );
                                const prod = productsById.get(group.productId);
                                return (
                                  <React.Fragment key={group.productId || 'no-product'}>
                                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                                      <TableCell colSpan={colSpan - 1} sx={{ py: 0.5 }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                          <ProductCell
                                            productId={group.productId}
                                            name={prod?.displayName}
                                            avatarUrl={prod?.avatarUrl}
                                          />
                                          <Typography variant="caption" color="text.secondary">
                                            {group.tenants.length} cliente
                                            {group.tenants.length !== 1 ? 's' : ''}
                                          </Typography>
                                        </Stack>
                                      </TableCell>
                                      <TableCell align="right" sx={{ py: 0.5 }}>
                                        <Typography variant="body2" fontWeight={600}>
                                          {formatCurrency(subtotal)}
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                    {group.tenants.map(renderTenantRow)}
                                  </React.Fragment>
                                );
                              })
                            ) : (
                              sortedTenants.map(renderTenantRow)
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    );
                  })()}
                </Box>
              )}

              {tabValue === 3 && (
                <Box p={2}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    spacing={1}
                    sx={{ mb: 2 }}
                  >
                    <Box>
                      <Typography variant="subtitle1">
                        Relatório anual de MRR · {yearReport?.year ?? year}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Previsto vs realizado por competência. Snapshots fechados são imutáveis;
                        competência atual é tempo real.
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={loadYearReport}
                        disabled={yearReportLoading}
                      >
                        {yearReportLoading ? 'Carregando…' : 'Recarregar'}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={exportYearReportCsv}
                        disabled={!yearReport}
                      >
                        CSV
                      </Button>
                    </Stack>
                  </Stack>

                  {yearReport && (
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" spacing={2} flexWrap="wrap">
                        <Chip
                          label={`Previsto: ${formatCurrency(yearReport.totals.estimatedMRR)}`}
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          label={`Recebido: ${formatCurrency(yearReport.totals.paidAmount)}`}
                          color="success"
                          variant="outlined"
                        />
                        <Chip
                          label={`Diferença: ${formatCurrency(yearReport.totals.diff)}`}
                          color={yearReport.totals.diff < 0 ? 'error' : 'success'}
                          variant="outlined"
                        />
                        <Chip
                          label={`% atingido: ${yearReport.totals.achievedPct.toFixed(1)}%`}
                          color={yearReport.totals.achievedPct >= 100 ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      </Stack>
                    </Box>
                  )}

                  {yearReportLoading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                      <CircularProgress />
                    </Box>
                  ) : !yearReport ? (
                    <Alert severity="info">
                      Clique em &quot;Recarregar&quot; para gerar o relatório anual.
                    </Alert>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell width={40} />
                            <TableCell>Competência</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Previsto (R$)</TableCell>
                            <TableCell align="right">Recebido (R$)</TableCell>
                            <TableCell align="right">Diferença</TableCell>
                            <TableCell align="right">% atingido</TableCell>
                            <TableCell align="right">Condomínio</TableCell>
                            <TableCell align="right">Gestão</TableCell>
                            <TableCell align="right">Institucional</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {yearReport.rows.map(r => {
                            const expanded = expandedYearRows.has(r.competence);
                            const hasDetail = (r.perTenant?.length ?? 0) > 0;
                            return (
                              <React.Fragment key={r.competence}>
                                <TableRow>
                                  <TableCell>
                                    {hasDetail ? (
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          setExpandedYearRows(prev => {
                                            const next = new Set(prev);
                                            if (next.has(r.competence)) next.delete(r.competence);
                                            else next.add(r.competence);
                                            return next;
                                          })
                                        }
                                      >
                                        {expanded ? <ExpandLess /> : <ExpandMore />}
                                      </IconButton>
                                    ) : null}
                                  </TableCell>
                                  <TableCell>{r.competence}</TableCell>
                                  <TableCell>
                                    <Tooltip
                                      title={
                                        <Box>
                                          <div>Origem: {r.source}</div>
                                          {r.closedAt ? (
                                            <div>
                                              Fechado em{' '}
                                              {new Date(r.closedAt).toLocaleString('pt-BR')}
                                            </div>
                                          ) : null}
                                        </Box>
                                      }
                                    >
                                      <Chip
                                        size="small"
                                        color={r.isClosed ? 'success' : 'warning'}
                                        icon={
                                          r.isClosed ? (
                                            <Lock sx={{ fontSize: 14 }} />
                                          ) : (
                                            <LockOpen sx={{ fontSize: 14 }} />
                                          )
                                        }
                                        label={r.isClosed ? 'Fechado' : 'Aberto'}
                                      />
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(r.estimatedMRR)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(r.paidAmount)}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography
                                      variant="body2"
                                      color={r.diff < 0 ? 'error.main' : 'success.main'}
                                    >
                                      {formatCurrency(r.diff)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">{r.achievedPct.toFixed(1)}%</TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(r.totals?.byPackageType?.condominio ?? 0)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(r.totals?.byPackageType?.gestao ?? 0)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(r.totals?.byPackageType?.institucional ?? 0)}
                                  </TableCell>
                                </TableRow>
                                {hasDetail && (
                                  <TableRow>
                                    <TableCell
                                      colSpan={isMultiProduct ? 11 : 10}
                                      sx={{ py: 0, borderBottom: 0 }}
                                    >
                                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                                        <Box sx={{ my: 1, ml: 4 }}>
                                          <Typography variant="caption" color="text.secondary">
                                            Detalhe por tenant
                                          </Typography>
                                          <Table size="small" sx={{ mt: 1 }}>
                                            <TableHead>
                                              <TableRow>
                                                {isMultiProduct && <TableCell>Produto</TableCell>}
                                                <TableCell>Tenant</TableCell>
                                                <TableCell>Pacote</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell align="right">Unidades</TableCell>
                                                <TableCell align="right">Clientes</TableCell>
                                                <TableCell align="right">MRR (R$)</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {[...(r.perTenant ?? [])]
                                                .sort((a, b) => {
                                                  if (isMultiProduct) {
                                                    const pa =
                                                      productsById.get(a.productId ?? '')
                                                        ?.displayName ?? '';
                                                    const pb =
                                                      productsById.get(b.productId ?? '')
                                                        ?.displayName ?? '';
                                                    const cmp = pa.localeCompare(pb, 'pt-BR');
                                                    if (cmp !== 0) return cmp;
                                                  }
                                                  return (a.tenantName ?? '').localeCompare(
                                                    b.tenantName ?? '',
                                                    'pt-BR'
                                                  );
                                                })
                                                .map(p => (
                                                  <TableRow key={`${r.competence}-${p.tenantId}`}>
                                                    {isMultiProduct && (
                                                      <TableCell>
                                                        {p.productId ? (
                                                          <ProductCell
                                                            productId={p.productId}
                                                            name={
                                                              productsById.get(p.productId)
                                                                ?.displayName
                                                            }
                                                            avatarUrl={
                                                              productsById.get(p.productId)
                                                                ?.avatarUrl
                                                            }
                                                          />
                                                        ) : (
                                                          '—'
                                                        )}
                                                      </TableCell>
                                                    )}
                                                    <TableCell>
                                                      <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        alignItems="center"
                                                      >
                                                        <ClientCell
                                                          name={p.tenantName}
                                                          tenantId={p.tenantId}
                                                          size="small"
                                                        />
                                                        {p.lockSource === 'invoice-paid' ? (
                                                          <Tooltip
                                                            title={
                                                              p.lockedAt
                                                                ? `Pago em ${new Date(
                                                                    p.lockedAt
                                                                  ).toLocaleDateString('pt-BR')}`
                                                                : 'Trancado por pagamento'
                                                            }
                                                          >
                                                            <Chip
                                                              size="small"
                                                              color="success"
                                                              icon={<Lock sx={{ fontSize: 12 }} />}
                                                              label="Pago"
                                                            />
                                                          </Tooltip>
                                                        ) : null}
                                                      </Stack>
                                                    </TableCell>
                                                    <TableCell>{p.packageType}</TableCell>
                                                    <TableCell>
                                                      <Chip
                                                        size="small"
                                                        label={p.status}
                                                        color={
                                                          p.status === 'trial' ? 'info' : 'default'
                                                        }
                                                      />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {p.totalUnits}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {p.totalClients}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatCurrency(p.monthlyAmount)}
                                                    </TableCell>
                                                  </TableRow>
                                                ))}
                                            </TableBody>
                                          </Table>
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {tabValue === 4 && (
                <Box p={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1">Despesas recorrentes</Typography>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => handleRecurringOpen()}
                    >
                      Nova despesa recorrente
                    </Button>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Descrição</TableCell>
                          <TableCell>Periodicidade</TableCell>
                          <TableCell>Início</TableCell>
                          <TableCell>Dia venc.</TableCell>
                          <TableCell align="right">Valor padrão (R$)</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Ações</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recurringExpenses.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                              Nenhuma despesa recorrente. Crie uma para gerar lançamentos
                              periódicos.
                            </TableCell>
                          </TableRow>
                        ) : (
                          recurringExpenses.map(r => (
                            <TableRow key={r._id}>
                              <TableCell>{r.description}</TableCell>
                              <TableCell>{recurrencePeriodLabel(r.recurrencePeriod)}</TableCell>
                              <TableCell>
                                {formatRecurrenceStartDate(r.recurrenceStartDate)}
                              </TableCell>
                              <TableCell>{r.dueDayOfMonth}</TableCell>
                              <TableCell align="right">
                                {formatCurrency(r.defaultAmountBRL)}
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  color={r.isActive ? 'success.main' : 'text.disabled'}
                                >
                                  {r.isActive ? 'Ativa' : 'Inativa'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color={r.isActive ? 'error' : 'success'}
                                  onClick={() => handleToggleRecurringActive(r)}
                                  sx={{ mr: 0.5 }}
                                >
                                  {r.isActive ? 'Desativar' : 'Ativar'}
                                </Button>
                                <IconButton
                                  size="small"
                                  title="Editar"
                                  onClick={() => handleRecurringOpen(r)}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  title="Remover"
                                  onClick={() => handleDeleteRecurring(r)}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Paper>
          </>
        )}

        <Dialog
          open={Boolean(editRevenueInvoiceId)}
          onClose={() => !editRevenueSaving && setEditRevenueInvoiceId(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Editar receita (billing)</DialogTitle>
          <DialogContent>
            {editRevenueLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="tenantId"
                  value={editRevenueTenantId}
                  onChange={e => setEditRevenueTenantId(e.target.value)}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Período de cobrança"
                  value={editRevenueBillingPeriod}
                  onChange={e => setEditRevenueBillingPeriod(e.target.value)}
                  margin="normal"
                  placeholder="YYYY-MM"
                />
                <TextField
                  fullWidth
                  type="date"
                  label="Vencimento"
                  value={editRevenueDueDate}
                  onChange={e => setEditRevenueDueDate(e.target.value)}
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="Valor (R$)"
                  value={editRevenueAmountRaw}
                  onChange={e => setEditRevenueAmountRaw(e.target.value)}
                  margin="normal"
                  placeholder="0,00"
                />
                <FormControl fullWidth margin="normal" size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editRevenueStatus}
                    onChange={e =>
                      setEditRevenueStatus(
                        e.target.value as 'draft' | 'pending' | 'paid' | 'overdue' | 'cancelled'
                      )
                    }
                    label="Status"
                  >
                    <MenuItem value="paid">paid (pago)</MenuItem>
                    <MenuItem value="pending">pending</MenuItem>
                    <MenuItem value="overdue">overdue</MenuItem>
                    <MenuItem value="draft">draft</MenuItem>
                    <MenuItem value="cancelled">cancelled</MenuItem>
                  </Select>
                </FormControl>
                {editRevenueStatus === 'paid' && (
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label="Pago em"
                    value={editRevenuePaidAt}
                    onChange={e => setEditRevenuePaidAt(e.target.value)}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
                <TextField
                  fullWidth
                  label="Descrição"
                  value={editRevenueDescription}
                  onChange={e => setEditRevenueDescription(e.target.value)}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Observação interna"
                  value={editRevenueNote}
                  onChange={e => setEditRevenueNote(e.target.value)}
                  margin="normal"
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditRevenueInvoiceId(null)} disabled={editRevenueSaving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={editRevenueSaving ? <CircularProgress size={16} /> : <Save />}
              onClick={handleSaveEditRevenue}
              disabled={editRevenueSaving || editRevenueLoading}
            >
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        <NfseEmissionFloatingStatus
          status={nfseEmissionStatus}
          onClose={() => setNfseEmissionStatus(null)}
        />
        <Dialog
          open={Boolean(nfPreviewInvoiceId)}
          onClose={() =>
            !issuingNfByInvoice[nfPreviewInvoiceId ?? ''] && setNfPreviewInvoiceId(null)
          }
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            {(nfPreviewInvoiceId
              ? (
                  nfsePayloadByInvoiceId[nfPreviewInvoiceId] as
                    | { emissionMode?: string }
                    | undefined
                )?.emissionMode
              : undefined) === 'nacional'
              ? 'Prévia NFS-e — Sistema Nacional (DPS)'
              : 'Prévia NFS-e — homologação (layout Biguaçu/SC)'}
          </DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Antes de solicitar a emissão no billing (que chamará a API do{' '}
              {(nfPreviewInvoiceId
                ? (
                    nfsePayloadByInvoiceId[nfPreviewInvoiceId] as
                      | { emissionMode?: string }
                      | undefined
                  )?.emissionMode
                : undefined) === 'nacional'
                ? 'Sistema Nacional NFS-e'
                : 'IPM'}
              ), confira os dados abaixo. O quadro só aparece quando a prévia do billing está válida
              e há dados reais (sem placeholders); números finais saem da prefeitura após
              autorização.
            </Alert>
            {nfPreviewGate.showConfigHint &&
              nfPreviewInvoiceId &&
              !nfsePayloadLoadingByInvoiceId[nfPreviewInvoiceId] && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="body2" display="block" gutterBottom>
                      {nfPreviewGate.validationErrors.length > 0
                        ? 'Ajuste a configuração fiscal global no billing / cadastro do tenant. Itens em falta:'
                        : nfsePayloadErrorByInvoiceId[nfPreviewInvoiceId]
                          ? 'Não foi possível carregar a prévia no billing:'
                          : 'A prévia não está válida para emissão.'}
                    </Typography>
                    {nfPreviewGate.validationErrors.length > 0 && (
                      <Box component="ul" sx={{ m: 0, pl: 2, mb: 1, typography: 'body2' }}>
                        {nfPreviewGate.validationErrors.map((msg, i) => (
                          <Box component="li" key={`${i}-${msg.slice(0, 40)}`}>
                            {msg}
                          </Box>
                        ))}
                      </Box>
                    )}
                    {nfsePayloadErrorByInvoiceId[nfPreviewInvoiceId] && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {nfsePayloadErrorByInvoiceId[nfPreviewInvoiceId]}
                      </Typography>
                    )}
                    <Link component={RouterLink} to="/configurations?tab=nfse" underline="hover">
                      Abrir Configurações — aba NFS-e
                    </Link>
                  </Box>
                </Alert>
              )}
            {(() => {
              const payload = nfPreviewInvoiceId
                ? nfsePayloadByInvoiceId[nfPreviewInvoiceId]
                : null;
              const p = (payload && typeof payload === 'object' ? payload : {}) as {
                emissionMode?: 'mock' | 'ipm' | 'nacional';
                providerInfo?: {
                  effective?: 'ipm' | 'nacional';
                  selected?: 'ipm' | 'nacional';
                  nacionalStartDate?: string | null;
                };
                nacional?: { ambiente?: 'homologacao' | 'producao'; idDps?: string };
                nodeEnv?: string;
                payload?: { issueParams?: { services?: Array<{ description?: string }> } };
              };
              const defaultDescription = String(
                p.payload?.issueParams?.services?.[0]?.description ?? ''
              );
              const description = nfseDescriptionDraft ?? defaultDescription;
              const isMock = p.emissionMode === 'mock';
              const isReal = p.emissionMode === 'ipm';
              const isNacional = p.emissionMode === 'nacional';
              const sched = p.providerInfo;
              const nacionalScheduled =
                !isNacional &&
                sched?.nacionalStartDate &&
                (sched.selected === 'nacional' || sched.nacionalStartDate);
              const nacionalReal = isNacional && p.nacional?.ambiente === 'producao';
              const tooLong = description.length > 2000;
              return (
                <>
                  {nacionalScheduled && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Esta emissão usará o <strong>IPM</strong>: a NFS-e Nacional está agendada para{' '}
                      <strong>
                        {new Date(`${sched?.nacionalStartDate}T12:00:00`).toLocaleDateString(
                          'pt-BR'
                        )}
                      </strong>{' '}
                      (a data de início prevalece sobre o provedor selecionado). Para emitir pelo
                      Nacional agora, limpe a data em{' '}
                      <Link component={RouterLink} to="/configurations?tab=nfse" underline="hover">
                        Configurações — NFS-e
                      </Link>
                      .
                    </Alert>
                  )}
                  {isNacional && (
                    <Alert severity={nacionalReal ? 'info' : 'warning'} sx={{ mb: 2 }}>
                      {nacionalReal ? (
                        <Typography variant="body2">
                          <strong>Sistema Nacional — produção:</strong> ao confirmar, a DPS é
                          assinada com o certificado A1 e a NFS-e é emitida de verdade (
                          <code>tpAmb=1</code>). A discriminação abaixo é a que será impressa na
                          nota.
                        </Typography>
                      ) : (
                        <Box>
                          <Typography variant="body2" fontWeight={600} gutterBottom>
                            Sistema Nacional — produção restrita (homologação): nenhuma NFS-e real
                            será emitida
                          </Typography>
                          <Box component="ol" sx={{ m: 0, pl: 2.5, typography: 'body2' }}>
                            <li>
                              A DPS (<code>tpAmb=2</code>) é assinada com o certificado A1 e enviada
                              à API de produção restrita, que valida de verdade as regras fiscais.
                            </li>
                            <li>
                              <strong>Aceita:</strong> a nota gerada tem número e chave de acesso,
                              mas <strong>não tem valor fiscal</strong>.
                            </li>
                            <li>
                              <strong>Recusada:</strong> o código e a descrição do erro do Sistema
                              Nacional são exibidos para você corrigir.
                            </li>
                            <li>
                              O contador de RPS do IPM <strong>não</strong> é consumido; o nDPS é
                              controlado separadamente.
                            </li>
                          </Box>
                        </Box>
                      )}
                    </Alert>
                  )}
                  {(isMock || isReal) && (
                    <Alert severity={isMock ? 'warning' : 'info'} sx={{ mb: 2 }}>
                      {isMock ? (
                        <Box>
                          <Typography variant="body2" fontWeight={600} gutterBottom>
                            Ambiente de teste ({p.nodeEnv || 'não-production'}) — nenhuma NFS-e real
                            será emitida
                          </Typography>
                          <Box component="ol" sx={{ m: 0, pl: 2.5, typography: 'body2' }}>
                            <li>
                              O billing valida os dados localmente (config fiscal, tomador,
                              endereço) e envia o XML ao IPM com{' '}
                              <code>{'<net:EnvioTeste>1</net:EnvioTeste>'}</code>. Esse modo{' '}
                              <strong>não emite</strong> nota: o IPM só responde se o XML foi aceito
                              ou o que está incorreto.
                            </li>
                            <li>
                              <strong>XML aceito:</strong> o billing gera uma NFS-e de exemplo
                              (número, código e PDF/XML sintéticos) e marca a fatura como emitida,
                              só para testar o fluxo (webhooks, links, telas).
                            </li>
                            <li>
                              <strong>XML recusado:</strong> nada é gerado; a emissão fica como
                              rejeitada e o retorno do IPM é exibido para você corrigir.
                            </li>
                            <li>
                              O contador de RPS é consumido a cada tentativa. Sem credenciais IPM no
                              ambiente local, defina <code>NFSE_IPM_TEST_DISABLED=true</code> no
                              billing para usar só o mock.
                            </li>
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2">
                          <strong>Production:</strong> ao confirmar, o SOAP é enviado ao IPM
                          (EnvioTeste=0) e a NFS-e é emitida de verdade na prefeitura. A
                          discriminação abaixo é a que será impressa na nota.
                        </Typography>
                      )}
                    </Alert>
                  )}
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Descrição do serviço (discriminação da NFS-e)
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={10}
                      value={description}
                      onChange={e => setNfseDescriptionDraft(e.target.value)}
                      disabled={Boolean(issuingNfByInvoice[nfPreviewInvoiceId ?? ''])}
                      error={tooLong || (description.trim() === '' && defaultDescription !== '')}
                      helperText={
                        tooLong
                          ? 'Máximo de 2000 caracteres'
                          : description.trim() === ''
                            ? 'Informe a descrição do serviço'
                            : `${description.length}/2000 — vale só para esta emissão; o template global (Configurações › NFS-e) não é alterado.`
                      }
                    />
                    {nfseDescriptionDraft !== null &&
                      nfseDescriptionDraft !== defaultDescription && (
                        <Button
                          size="small"
                          sx={{ mt: 1 }}
                          onClick={() => setNfseDescriptionDraft(null)}
                        >
                          Restaurar descrição padrão
                        </Button>
                      )}
                  </Paper>
                </>
              );
            })()}
            {(() => {
              const row = billingInvoices.find(r => r.id === nfPreviewInvoiceId);
              const tenantName = row ? tenantInfoById[row.tenantId]?.companyName : undefined;
              const tenantCnpj = row ? tenantInfoById[row.tenantId]?.cnpj : null;
              const payloadLoading = nfPreviewInvoiceId
                ? Boolean(nfsePayloadLoadingByInvoiceId[nfPreviewInvoiceId])
                : false;
              const payloadErr = nfPreviewInvoiceId
                ? nfsePayloadErrorByInvoiceId[nfPreviewInvoiceId]
                : null;
              const payload = nfPreviewInvoiceId
                ? nfsePayloadByInvoiceId[nfPreviewInvoiceId]
                : null;
              const issueParams =
                payload && typeof payload === 'object'
                  ? (payload as { payload?: { issueParams?: Record<string, unknown> } }).payload
                      ?.issueParams
                  : undefined;
              const isNacionalPreview =
                payload &&
                typeof payload === 'object' &&
                (payload as { emissionMode?: string }).emissionMode === 'nacional';
              const nacionalDanfseProps =
                row && isNacionalPreview
                  ? buildNacionalDanfseProps({
                      amountCents: row.amountCents,
                      preview: payload as Record<string, unknown>,
                      billingCfg: nfPreviewBillingCfg
                    })
                  : null;
              const homologacaoProps =
                row &&
                !isNacionalPreview &&
                buildBiguacuHomologacaoProps({
                  row: {
                    id: row.id,
                    tenantId: row.tenantId,
                    billingPeriod: row.billingPeriod,
                    amountCents: row.amountCents,
                    dueDate: row.dueDate,
                    paidAt: row.paidAt
                  },
                  issueParams,
                  billingCfg: nfPreviewBillingCfg
                });
              return (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <>
                    {homologacaoProps && (
                      <Box sx={{ mb: 3, overflow: 'auto', maxHeight: '70vh' }}>
                        <NfseBiguacuHomologacaoMock {...homologacaoProps} />
                      </Box>
                    )}
                    {nacionalDanfseProps && (
                      <Box sx={{ mb: 3, overflow: 'auto', maxHeight: '70vh' }}>
                        <NfseNacionalDanfsePreview data={nacionalDanfseProps} />
                      </Box>
                    )}
                    {!homologacaoProps &&
                      !isNacionalPreview &&
                      row &&
                      payload &&
                      typeof payload === 'object' &&
                      (payload as { success?: boolean }).success &&
                      !payloadLoading &&
                      !payloadErr && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Box component="span">
                            O layout visual não foi montado: verifique se o beneficiário final
                            (Inter) está completo na config global do billing e se os dados do
                            tomador/endereço estão no cadastro do tenant. Use{' '}
                            <Link
                              component={RouterLink}
                              to="/configurations?tab=nfse"
                              underline="hover"
                            >
                              Configurações — NFS-e
                            </Link>
                            .
                          </Box>
                        </Alert>
                      )}
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ mt: homologacaoProps || nacionalDanfseProps ? 2 : 0 }}
                    >
                      Resumo e payloads técnicos
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Tomador
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {tenantName || row?.tenantId || '—'}
                        </Typography>
                        {tenantCnpj && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            CNPJ: {tenantCnpj}
                          </Typography>
                        )}
                        {row?.tenantId && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Tenant ID: <CopyableId value={row.tenantId} label="tenantId" />
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Fatura / Competência
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {row?.billingPeriod ?? '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Invoice ID: {row?.id ?? '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Vencimento
                        </Typography>
                        <Typography variant="body2">
                          {row?.dueDate ? new Date(row.dueDate).toLocaleDateString('pt-BR') : '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Valor do serviço
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {formatCurrency((row?.amountCents ?? 0) / 100)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Dados que o vover-admin enviará ao vover-billing
                        </Typography>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            bgcolor: theme => theme.palette.grey[50],
                            fontFamily: 'monospace'
                          }}
                        >
                          <Typography variant="body2">
                            {JSON.stringify({ invoiceId: row?.id ?? '' }, null, 2)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12}>
                        <Box>
                          <div>
                            <>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {isNacionalPreview
                                  ? 'Payload que o vover-billing enviará ao Sistema Nacional (prévia)'
                                  : 'Payload que o vover-billing enviará ao IPM (prévia)'}
                              </Typography>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 1.5,
                                  bgcolor: theme => theme.palette.grey[50],
                                  fontFamily: 'monospace'
                                }}
                              >
                                {payloadLoading ? (
                                  <Box display="flex" justifyContent="center" py={1}>
                                    <CircularProgress size={18} />
                                  </Box>
                                ) : payloadErr ? (
                                  <Typography variant="body2" color="text.secondary">
                                    {payloadErr}
                                  </Typography>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    component="pre"
                                    sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                  >
                                    {JSON.stringify(payload ?? null, null, 2)}
                                  </Typography>
                                )}
                              </Paper>

                              {!payloadLoading &&
                                !payloadErr &&
                                payload &&
                                typeof payload === 'object' && (
                                  <>
                                    {isNacionalPreview && (payload as any)?.request && (
                                      <>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                          sx={{ mt: 2 }}
                                        >
                                          Requisição HTTP ao Sistema Nacional
                                        </Typography>
                                        <Paper
                                          variant="outlined"
                                          sx={{
                                            p: 1.5,
                                            bgcolor: theme => theme.palette.grey[50],
                                            fontFamily: 'monospace'
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            component="pre"
                                            sx={{
                                              m: 0,
                                              whiteSpace: 'pre-wrap',
                                              wordBreak: 'break-word'
                                            }}
                                          >
                                            {JSON.stringify((payload as any).request, null, 2)}
                                          </Typography>
                                        </Paper>
                                      </>
                                    )}
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                      sx={{ mt: 2 }}
                                    >
                                      {isNacionalPreview
                                        ? 'XML da DPS que será enviado ao Sistema Nacional (sem assinatura; o XMLDSIG com o A1 é aplicado no envio)'
                                        : 'XML SOAP que será enviado ao IPM (formatado)'}
                                    </Typography>
                                    <Paper
                                      variant="outlined"
                                      sx={{
                                        p: 1.5,
                                        bgcolor: theme => theme.palette.grey[50],
                                        fontFamily: 'monospace'
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        component="pre"
                                        sx={{
                                          m: 0,
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word'
                                        }}
                                      >
                                        {(() => {
                                          const xml = (payload as any)?.payload?.soapXml;
                                          if (typeof xml === 'string' && xml.trim())
                                            return formatXmlForPreview(xml);
                                          return '— (sem XML: verifique os validationErrors / config fiscal NFSe no billing)';
                                        })()}
                                      </Typography>
                                    </Paper>

                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                      sx={{ mt: 2 }}
                                    >
                                      {isNacionalPreview
                                        ? 'Retorno do Sistema Nacional (simulação — só existe “real” após emitir)'
                                        : 'Retorno do IPM (simulação — só existe “real” após emitir)'}
                                    </Typography>
                                    <Paper
                                      variant="outlined"
                                      sx={{
                                        p: 1.5,
                                        bgcolor: theme => theme.palette.grey[50],
                                        fontFamily: 'monospace'
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        component="pre"
                                        sx={{
                                          m: 0,
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word'
                                        }}
                                      >
                                        {JSON.stringify(
                                          (() => {
                                            const issueParams =
                                              (payload as any)?.payload?.issueParams || {};
                                            if (isNacionalPreview) {
                                              const nac = (payload as any)?.nacional || {};
                                              return {
                                                simulated: true,
                                                ambiente:
                                                  nac.tpAmb === '1'
                                                    ? 'Produção'
                                                    : 'Produção restrita (homologação)',
                                                http: 201,
                                                idDps: nac.idDps ?? null,
                                                chaveAcesso:
                                                  '<50 dígitos — gerada pelo Sistema Nacional>',
                                                nfseXmlGZipB64:
                                                  '<NFS-e (XML) com a DPS embutida — GZip + Base64>',
                                                alertas: [],
                                                erros: [],
                                                vover: {
                                                  status: 'authorized',
                                                  numero: '<nNFSe>',
                                                  codigoVerificacao: '<chave de acesso>',
                                                  nDps: nac.dps?.nDps ?? null,
                                                  serie: nac.dps?.serie ?? null,
                                                  competencia: nac.dps?.dCompet ?? null,
                                                  pdfUrl: 'DANFSe oficial (ADN) após emitir',
                                                  xmlUrl: 'XML da NFS-e após emitir'
                                                },
                                                observation:
                                                  'Número, chave e documentos só são gerados pelo Sistema Nacional após o envio. Em caso de rejeição, a resposta traz erros[] com código e descrição.'
                                              };
                                            }
                                            const tenantNameLocal =
                                              tenantName ||
                                              issueParams?.customerName ||
                                              row?.tenantId ||
                                              '—';
                                            const nowIso = new Date().toISOString();
                                            return {
                                              simulated: true,
                                              receivedAt: nowIso,
                                              success: true,
                                              invoiceId:
                                                (payload as any)?.invoiceId ?? row?.id ?? '',
                                              tenantId:
                                                (payload as any)?.tenantId ?? row?.tenantId ?? '',
                                              tomador: {
                                                name: tenantNameLocal,
                                                cnpjCpf: issueParams?.customerCnpjCpf ?? null
                                              },
                                              nfse: {
                                                numero: 'SIMULADO-000000',
                                                codigoVerificacao: 'SIMULADO-ABCDEFG',
                                                competencia: row?.billingPeriod ?? null,
                                                emissao: issueParams?.emissionDateYmd ?? null,
                                                rps: {
                                                  numero: issueParams?.rpsNumero ?? null,
                                                  serie:
                                                    issueParams?.fiscalConfig?.rpsSeries ?? null
                                                },
                                                links: {
                                                  pdfUrl: null,
                                                  xmlUrl: null
                                                }
                                              },
                                              observation:
                                                'Valores reais (número/código/link) só são gerados pelo IPM após o envio.'
                                            };
                                          })(),
                                          null,
                                          2
                                        )}
                                      </Typography>
                                    </Paper>
                                  </>
                                )}
                            </>
                          </div>
                        </Box>
                      </Grid>
                    </Grid>
                  </>
                </Paper>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setNfPreviewInvoiceId(null)}
              disabled={Boolean(issuingNfByInvoice[nfPreviewInvoiceId ?? ''])}
            >
              {nfseIssueResult?.outcome === 'success' ? 'Fechar' : 'Cancelar'}
            </Button>
            <Button
              variant="contained"
              onClick={() =>
                nfPreviewInvoiceId &&
                handleIssueNfse(
                  nfPreviewInvoiceId,
                  nfseDescriptionDraft !== null ? nfseDescriptionDraft : undefined
                )
              }
              disabled={
                !nfPreviewInvoiceId ||
                Boolean(issuingNfByInvoice[nfPreviewInvoiceId ?? '']) ||
                nfPreviewGate.issueBlocked ||
                nfseIssueResult?.outcome === 'success' ||
                nfseIssueResult?.outcome === 'pending' ||
                (nfseDescriptionDraft !== null &&
                  (nfseDescriptionDraft.trim() === '' || nfseDescriptionDraft.length > 2000))
              }
            >
              {nfPreviewInvoiceId && issuingNfByInvoice[nfPreviewInvoiceId] ? (
                <CircularProgress size={22} />
              ) : (
                'Emitir NF'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(paymentDateModal)}
          onClose={() => !submittingPaymentDate && setPaymentDateModal(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Data de pagamento</DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Data"
              type="date"
              value={paymentDateValue}
              onChange={e => setPaymentDateValue(e.target.value)}
              InputLabelProps={{ shrink: true }}
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPaymentDateModal(null)} disabled={submittingPaymentDate}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handlePaymentDateConfirm}
              disabled={submittingPaymentDate}
            >
              {submittingPaymentDate ? <CircularProgress size={24} /> : 'Confirmar'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(ipmSyncDialogInvoiceId)}
          onClose={() => setIpmSyncDialogInvoiceId(null)}
          maxWidth="xs"
          fullWidth
          sx={theme => ({ zIndex: theme.zIndex.modal + 10 })}
        >
          <DialogTitle>Consultar NFS-e no IPM</DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              O billing consulta a NFS-e pelo número do <strong>RPS</strong> (e série/tipo da config
              fiscal). Se a emissão deu certo no IPM mas não há registro na auditoria interna,
              informe abaixo o <strong>número do RPS</strong> impresso na NFS-e no portal (ex.:
              1000). Deixe em branco para tentar usar só a auditoria, se existir.
            </Typography>
            <TextField
              fullWidth
              label="Número do RPS (opcional)"
              value={ipmSyncRpsInput}
              onChange={e => setIpmSyncRpsInput(e.target.value)}
              placeholder="Ex.: 1000"
              type="text"
              inputMode="numeric"
              margin="dense"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIpmSyncDialogInvoiceId(null)}>Cancelar</Button>
            <Button
              variant="contained"
              disabled={!ipmSyncDialogInvoiceId}
              onClick={() => {
                const id = ipmSyncDialogInvoiceId;
                if (!id) return;
                const raw = ipmSyncRpsInput.trim().replace(/\D/g, '');
                let body: { rpsNumero?: number } | undefined;
                if (raw) {
                  const n = Math.floor(Number(raw));
                  if (!Number.isFinite(n) || n < 1) {
                    showError(
                      'Informe um número de RPS válido (inteiro ≥ 1) ou deixe o campo vazio.'
                    );
                    return;
                  }
                  body = { rpsNumero: n };
                }
                setIpmSyncDialogInvoiceId(null);
                setIpmSyncRpsInput('');
                void handleSyncNfseFromIpm(id, body);
              }}
            >
              Consultar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={processExpensesModalOpen}
          onClose={() => !processingExpenses && setProcessExpensesModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Gerar despesas do ano</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Selecione as despesas recorrentes e edite o valor. Serão gerados lançamentos do mês
              atual até dezembro de {year}, respeitando a periodicidade e a data de início de cada
              uma.
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={
                          recurringExpenses?.filter(r => r.isActive).length ===
                            selectedRecurringIds.size && selectedRecurringIds.size > 0
                        }
                        indeterminate={
                          selectedRecurringIds.size > 0 &&
                          selectedRecurringIds.size <
                            (recurringExpenses?.filter(r => r.isActive).length ?? 0)
                        }
                        onChange={() =>
                          handleProcessExpensesSelectAll(
                            recurringExpenses?.filter(r => r.isActive).map(r => r._id) ?? []
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell>Periodicidade</TableCell>
                    <TableCell>Dia venc.</TableCell>
                    <TableCell align="right" sx={{ minWidth: 120 }}>
                      Valor (R$)
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recurringExpenses
                    ?.filter(r => r.isActive)
                    .map(r => {
                      const alreadyGenerated = false;
                      return (
                        <TableRow key={r._id}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedRecurringIds.has(r._id)}
                              disabled={alreadyGenerated}
                              onChange={() =>
                                !alreadyGenerated && handleProcessExpensesToggle(r._id)
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{r.description}</Typography>
                            {alreadyGenerated && (
                              <Typography variant="caption" color="text.secondary">
                                Já gerada
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" display="block">
                              Início: {formatRecurrenceStartDate(r.recurrenceStartDate)}
                            </Typography>
                          </TableCell>
                          <TableCell>{recurrencePeriodLabel(r.recurrencePeriod)}</TableCell>
                          <TableCell>{r.dueDayOfMonth}</TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              value={
                                processExpenseAmounts[r._id] ??
                                numberToEditableString(r.defaultAmountBRL)
                              }
                              onChange={e =>
                                handleProcessExpensesAmountChange(r._id, e.target.value)
                              }
                              disabled={alreadyGenerated}
                              placeholder="0,00"
                              inputProps={{ style: { textAlign: 'right' } }}
                              sx={{ maxWidth: 120 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setProcessExpensesModalOpen(false)}
              disabled={processingExpenses}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleProcessExpensesSubmit}
              disabled={processingExpenses || selectedRecurringIds.size === 0}
            >
              {processingExpenses ? <CircularProgress size={24} /> : 'Gerar selecionadas'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={adjustDialogOpen}
          onClose={() => !adjusting && setAdjustDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Ajustar valor real</DialogTitle>
          <DialogContent>
            {adjustEntry && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {adjustEntry.description} — valor cobrado no dia da cotação:
              </Typography>
            )}
            <TextField
              fullWidth
              label="Valor (R$)"
              value={adjustAmountRaw}
              onChange={e => setAdjustAmountRaw(e.target.value)}
              margin="normal"
              placeholder="0,00"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAdjustDialogOpen(false)} disabled={adjusting}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleAdjustSubmit} disabled={adjusting}>
              {adjusting ? <CircularProgress size={24} /> : 'Salvar'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={manualDialogOpen}
          onClose={() => !creatingManual && setManualDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Adicionar despesa manual</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Dia"
                  type="number"
                  value={manualDay}
                  onChange={e =>
                    setManualDay(Math.min(31, Math.max(1, parseInt(e.target.value, 10) || 1)))
                  }
                  inputProps={{ min: 1, max: 31 }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel>Mês</InputLabel>
                  <Select
                    value={manualMonth}
                    onChange={e => setManualMonth(Number(e.target.value))}
                    label="Mês"
                  >
                    {MONTH_NAMES.map((name, i) => (
                      <MenuItem key={i} value={i + 1}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel>Ano</InputLabel>
                  <Select
                    value={manualYear}
                    onChange={e => setManualYear(Number(e.target.value))}
                    label="Ano"
                  >
                    {years.map(y => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label="Descrição"
              value={manualDesc}
              onChange={e => setManualDesc(e.target.value)}
              margin="normal"
              {...(manualRecurringId && {
                helperText: 'Opcional — será usada a descrição da despesa recorrente',
                placeholder: '(usa descrição da recorrente)'
              })}
            />
            <FormControl fullWidth margin="normal" size="small">
              <InputLabel shrink>Despesa recorrente (opcional)</InputLabel>
              <Select
                value={manualRecurringId}
                onChange={e => setManualRecurringId(e.target.value)}
                label="Despesa recorrente (opcional)"
                displayEmpty
              >
                <MenuItem value="">
                  <em>Nenhuma</em>
                </MenuItem>
                {recurringExpenses.map(r => (
                  <MenuItem key={r._id} value={r._id}>
                    {r.description}
                    {!r.isActive && ' (inativa)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Valor (R$)"
              value={manualAmountRaw}
              onChange={e => setManualAmountRaw(e.target.value)}
              margin="normal"
              placeholder="0,00"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setManualDialogOpen(false)} disabled={creatingManual}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleManualSubmit} disabled={creatingManual}>
              {creatingManual ? <CircularProgress size={24} /> : 'Adicionar'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={manualRevenueOpen}
          onClose={() => !manualRevenueSaving && setManualRevenueOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Registrar receita realizada (manual)</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Lançamento administrativo no vover-billing. Não é possível duplicar o mesmo período de
              cobrança para o mesmo cliente no mesmo produto.
            </Alert>
            <FormControl fullWidth margin="normal" size="small">
              <InputLabel id="manual-revenue-product-select">Produto</InputLabel>
              <Select
                labelId="manual-revenue-product-select"
                value={manualRevenueApplicationId}
                onChange={e => {
                  setManualRevenueApplicationId(e.target.value as string);
                  setManualRevenueTenantId('');
                }}
                label="Produto"
              >
                <MenuItem value="">
                  <em>Selecione um produto</em>
                </MenuItem>
                {productOptions.map(p => (
                  <MenuItem key={p.productId} value={p.productId}>
                    <ProductCell
                      productId={p.productId}
                      name={p.displayName}
                      avatarUrl={p.avatarUrl}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              fullWidth
              margin="normal"
              size="small"
              disabled={!manualRevenueApplicationId}
            >
              <InputLabel id="manual-revenue-tenant-select">Cliente</InputLabel>
              <Select
                labelId="manual-revenue-tenant-select"
                value={
                  manualRevenueTenants.some(t => t.tenantId === manualRevenueTenantId)
                    ? manualRevenueTenantId
                    : ''
                }
                onChange={e => setManualRevenueTenantId(e.target.value as string)}
                label="Cliente"
              >
                <MenuItem value="">
                  <em>
                    {manualRevenueApplicationId
                      ? 'Selecione um cliente'
                      : 'Selecione o produto primeiro'}
                  </em>
                </MenuItem>
                {manualRevenueTenants.map(t => (
                  <MenuItem key={t.tenantId} value={t.tenantId}>
                    <ClientCell name={t.companyName} tenantId={t.tenantId} size="small" />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal" size="small" disabled={!manualRevenueTenantId}>
              <InputLabel id="manual-revenue-period-select">Período de cobrança</InputLabel>
              <Select
                labelId="manual-revenue-period-select"
                value={manualRevenueBillingPeriod}
                onChange={e => setManualRevenueBillingPeriod(e.target.value as string)}
                label="Período de cobrança"
              >
                {manualRevenuePeriods.length === 0 ? (
                  <MenuItem value="">
                    <em>Sem períodos disponíveis</em>
                  </MenuItem>
                ) : (
                  manualRevenuePeriods.map(p => (
                    <MenuItem key={p.key} value={p.key}>
                      {p.label}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -0.5 }}>
              Opções de {manualRevenueTenantId ? 'MM-YYYY' : 'MM-YYYY (selecione o cliente)'} até o
              fim do próximo ano.
            </Typography>
            <TextField
              fullWidth
              type="date"
              label="Vencimento"
              value={manualRevenueDueDate}
              onChange={e => setManualRevenueDueDate(e.target.value)}
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Valor (R$)"
              value={manualRevenueAmountRaw}
              onChange={e => setManualRevenueAmountRaw(e.target.value)}
              margin="normal"
              placeholder="0,00"
            />
            <FormControl fullWidth margin="normal" size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={manualRevenueStatus}
                onChange={e =>
                  setManualRevenueStatus(
                    e.target.value as 'draft' | 'pending' | 'paid' | 'overdue' | 'cancelled'
                  )
                }
                label="Status"
              >
                <MenuItem value="paid">paid (pago)</MenuItem>
                <MenuItem value="pending">pending</MenuItem>
                <MenuItem value="overdue">overdue</MenuItem>
                <MenuItem value="draft">draft</MenuItem>
                <MenuItem value="cancelled">cancelled</MenuItem>
              </Select>
            </FormControl>
            {manualRevenueStatus === 'paid' && (
              <TextField
                fullWidth
                type="datetime-local"
                label="Pago em"
                value={manualRevenuePaidAt}
                onChange={e => setManualRevenuePaidAt(e.target.value)}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                helperText="Preenchido automaticamente com a data/hora atual."
              />
            )}
            <TextField
              fullWidth
              label="Descrição (opcional)"
              value={manualRevenueDescription}
              onChange={e => setManualRevenueDescription(e.target.value)}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Observação interna (opcional)"
              value={manualRevenueNote}
              onChange={e => setManualRevenueNote(e.target.value)}
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setManualRevenueOpen(false)} disabled={manualRevenueSaving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleManualRevenueSubmit}
              disabled={manualRevenueSaving}
            >
              {manualRevenueSaving ? <CircularProgress size={24} /> : 'Registrar'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={recurringDialogOpen}
          onClose={() => !savingRecurring && setRecurringDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            {recurringEdit ? 'Editar despesa recorrente' : 'Nova despesa recorrente'}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Descrição (ex: Railway, Vercel)"
              value={recurringDesc}
              onChange={e => setRecurringDesc(e.target.value)}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Valor padrão (R$)"
              value={recurringAmountRaw}
              onChange={e => setRecurringAmountRaw(e.target.value)}
              margin="normal"
              placeholder="0,00"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="recurring-period-label">Periodicidade</InputLabel>
              <Select
                labelId="recurring-period-label"
                label="Periodicidade"
                value={recurringPeriod}
                onChange={e => setRecurringPeriod(e.target.value as RecurrencePeriod)}
              >
                {RECURRENCE_PERIOD_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Início da recorrência"
              type="date"
              value={recurringStartDate}
              onChange={e => setRecurringStartDate(e.target.value)}
              margin="normal"
              InputLabelProps={{ shrink: true }}
              helperText="A partir desta data passam a contar os ciclos da periodicidade escolhida"
            />
            <TextField
              fullWidth
              label="Dia do vencimento (1-31)"
              type="number"
              value={recurringDay}
              onChange={e =>
                setRecurringDay(Math.min(31, Math.max(1, parseInt(e.target.value, 10) || 1)))
              }
              margin="normal"
              inputProps={{ min: 1, max: 31 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRecurringDialogOpen(false)} disabled={savingRecurring}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleRecurringSubmit} disabled={savingRecurring}>
              {savingRecurring ? (
                <CircularProgress size={24} />
              ) : recurringEdit ? (
                'Salvar'
              ) : (
                'Criar'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal: Semear competência histórica de MRR */}
        <Dialog
          open={seedDialogOpen}
          onClose={() => (seedSaving ? null : setSeedDialogOpen(false))}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Semear competência histórica</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Semear MRR</strong> serve apenas para preencher histórico de competências
                anteriores à implantação do cálculo automático. Ele não emite cobrança, não altera
                faturas e não muda preço de cliente.
              </Typography>
              <Typography variant="body2">
                Usabilidade real: deixar relatórios anuais e comparativos sem buracos históricos
                quando você já conhece o MRR esperado daquele mês. Para meses atuais/futuros,
                prefira fechar competência ou deixar o cálculo em tempo real.
              </Typography>
            </Alert>
            <TextField
              fullWidth
              label="Competência (YYYY-MM)"
              placeholder="2026-04"
              value={seedCompetenceInput}
              onChange={e => setSeedCompetenceInput(e.target.value.trim())}
              margin="normal"
              inputProps={{ pattern: '\\d{4}-(0[1-9]|1[0-2])' }}
              helperText="Mês que será registrado no histórico. Ex.: 2026-04 para abril de 2026."
            />
            <TextField
              fullWidth
              label="MRR previsto (R$)"
              value={seedEstimatedRaw}
              onChange={e => setSeedEstimatedRaw(e.target.value)}
              margin="normal"
              helperText="Valor total esperado de receita recorrente mensal para essa competência."
            />
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                label="Tenants ativos (opcional)"
                type="number"
                value={seedActiveTenants}
                onChange={e => setSeedActiveTenants(e.target.value)}
                margin="normal"
                inputProps={{ min: 0 }}
                helperText="Só informativo para relatório; não cria nem ativa cliente."
              />
              <TextField
                fullWidth
                label="Tenants em trial (opcional)"
                type="number"
                value={seedTrialTenants}
                onChange={e => setSeedTrialTenants(e.target.value)}
                margin="normal"
                inputProps={{ min: 0 }}
                helperText="Só informativo para acompanhar base em teste."
              />
            </Stack>
            <TextField
              fullWidth
              label="Notas (opcional)"
              multiline
              minRows={2}
              value={seedNotes}
              onChange={e => setSeedNotes(e.target.value)}
              margin="normal"
              helperText="Registre a origem do número, por exemplo planilha, fechamento antigo ou conferência manual."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSeedDialogOpen(false)} disabled={seedSaving}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleSubmitSeed} disabled={seedSaving}>
              {seedSaving ? <CircularProgress size={22} /> : 'Gravar snapshot'}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    </Box>
  );
};
